import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Alert,
  LinearProgress,
  Badge,
  Tooltip as MuiTooltip,
  Tooltip,
  Menu,
  Drawer,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  Skeleton,
  CircularProgress,
  Rating,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
} from '@mui/material';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell, 
  ComposedChart, 
  Line,
  LineChart,
  AreaChart,
  Area
} from 'recharts';
import { getProductPrimaryImageUrl, getProductImageUrls, getFullImageUrl } from '../../utils/imageUtils';
import {
  Dashboard as DashboardIcon,
  People,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Add,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  AttachMoney,
  LocalShipping,
  Star,
  Upload,
  Download,
  Refresh,
  Analytics,
  Receipt,
  Warning,
  CheckCircle,
  Pending,
  Cancel,
  Search,
  FilterList,
  Inventory,
  Store,
  Assessment,
  Security,
  Settings,
  Notifications,
  Home,
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Business,
  MonetizationOn,
  Timeline,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  ShowChart,
  AccountBalance,
  CreditCard,
  LocalOffer,
  Group,
  Category,
  Report,
  HomeWork,
  Assignment,
  ArrowUpward,
  ArrowDownward,
  Person,
  ContactMail,
  LocationOn,
  Description,
  Close,
  ExpandMore,
  PhotoCamera,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import VisitRequestsPage from './VisitRequestsPage';
import AdminOrdersPage from './AdminOrdersPage';
import AdminLotRequestsPage from './AdminLotRequestsPage';
import FeaturedProductsTab from '../../components/admin/FeaturedProductsTab';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  fetchPendingSellers,
  fetchApprovedSellers,
  fetchRejectedSellers,
  fetchPendingProducts,
  fetchAdminApprovedProducts,
  fetchAdminRejectedProducts,
  fetchAdminAnalytics,
  fetchMonthlyRevenueFromCompletedOrders,
  approveSeller,
  rejectSeller,
  approveProduct,
  rejectProduct,
  clearAdminError
} from '../../store/adminSlice';
import {
  generateSalesReport,
  generateProductReport,
  generateUserReport,
  fetchUserAnalytics,
  fetchPaymentAnalytics
} from '../../store/slices/analyticsSlice';
import * as XLSX from 'xlsx';
import BannerManagement from '../../components/admin/BannerManagement';
import ReportedAndBlocked from '../../components/admin/ReportedAndBlocked';
import { productService } from '../../services/productService';
import analyticsService from '../../services/analyticsService';
import { orderService } from '../../services/orderService';
import auditService from '../../services/auditService';

const CATEGORY_COLOR_PALETTE = [
  '#4F46E5',
  '#2563EB',
  '#0EA5E9',
  '#14B8A6',
  '#10B981',
  '#22C55E',
  '#F59E0B',
  '#F97316',
  '#EF4444',
  '#EC4899',
  '#8B5CF6',
  '#6366F1'
];

const toOpaqueColor = (value) => {
  if (!value) return null;
  const rgbaMatch = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/i);
  if (rgbaMatch) {
    return `rgb(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]})`;
  }
  return value;
};

const CATEGORY_LABEL_RADIAN = Math.PI / 180;

// StatCard component defined outside to prevent recreation on every render
const StatCard = React.memo(({ 
  title, 
  value, 
  icon, 
  trend, 
  trendValue, 
  color = 'primary', 
  period, 
  loading = false 
}) => (
  <Card sx={{ 
    height: '100%', 
    position: 'relative', 
    overflow: 'visible',
    border: '2px solid #1e3a8a',
    borderRadius: 2,
    '&:hover': {
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.3s ease-in-out'
  }}>
    <CardContent>
      {loading ? (
        <Box>
          <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="80%" height={40} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: '50%', float: 'right', mt: -8 }} />
          <Skeleton variant="text" width="40%" height={20} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: '#000', fontWeight: 600, fontSize: '0.95rem' }} gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
              {typeof value === 'number' && value > 1000 ? 
                value > 1000000 ? `₹${(value / 1000000).toFixed(1)}M` :
                `${(value / 1000).toFixed(1)}K` : 
                value.toLocaleString()
              }
            </Typography>
            {trend !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {trend > 0 ? (
                  <TrendingUp color="success" sx={{ mr: 0.5, fontSize: 16 }} />
                ) : (
                  <TrendingDown color="error" sx={{ mr: 0.5, fontSize: 16 }} />
                )}
                <Typography
                  variant="body2"
                  color={trend > 0 ? 'success.main' : 'error.main'}
                  sx={{ fontWeight: 600 }}
                >
                  {Math.abs(trend)}%
                </Typography>
                {period && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {period}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Box>
      )}
    </CardContent>
  </Card>
), (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.title === nextProps.title &&
    prevProps.value === nextProps.value &&
    prevProps.trend === nextProps.trend &&
    prevProps.trendValue === nextProps.trendValue &&
    prevProps.color === nextProps.color &&
    prevProps.period === nextProps.period &&
    prevProps.loading === nextProps.loading
    // Intentionally skip icon comparison as it's always a new JSX element
  );
});

StatCard.displayName = 'StatCard';

// Helper function to determine product submission type
const getProductSubmissionType = (product) => {
  // Priority 1: Check if product has approval comments (was rejected before) - this indicates RESUBMISSION
  // When a product is resubmitted, we preserve approvalComments to identify it
  // When a product is edited, approvalComments is cleared (set to null)
  if (product.approvalComments && product.approvalComments.trim() !== '') {
    return 'resubmitted';
  }
  
  // Priority 2: Check if product was previously rejected (has rejection history) - this indicates RESUBMISSION
  // When a product is resubmitted, rejectedAt/rejectedBy should still be present in the backend
  if (product.rejectedAt || (product.rejectedBy && product.rejectedBy !== null && product.rejectedBy !== undefined)) {
    return 'resubmitted';
  }
  
  // Priority 3: Check if product was previously approved - this indicates EDIT
  // When an approved product is edited, it goes back to pending
  // Note: When resubmitting, approvedAt/approvedBy are cleared, so this check comes after rejection check
  if (product.approvedAt || (product.approvedBy && product.approvedBy !== null && product.approvedBy !== undefined)) {
    return 'edited';
  }
  
  // Priority 4: Check if product was updated significantly after creation
  // If a product was created and then updated much later (more than 24 hours), it's likely an edit
  // This catches cases where approvedAt/approvedBy might not be available
  if (product.updatedAt && product.createdAt) {
    const createdAt = new Date(product.createdAt);
    const updatedAt = new Date(product.updatedAt);
    const timeDiff = updatedAt.getTime() - createdAt.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);
    
    // If product was updated more than 24 hours after creation and is now pending,
    // it's likely an EDIT (a new product wouldn't be edited that long after creation)
    // But only if it doesn't have rejection history (which would indicate resubmission)
    if (hoursDiff > 24 && product.approvalStatus === 'PENDING') {
      return 'edited';
    }
  }
  
  // Default to new product (recently created, no approval/rejection history, no significant updates)
  return 'new';
};

const ProductsTabContent = React.memo(
  ({
    pendingCount,
    pendingProductsLoading,
    approvedProducts,
    approvedProductsLoading,
    rejectedProducts,
    rejectedProductsLoading,
    error,
    sortedProducts,
    onRefresh,
    onSort,
    sortField,
    sortDirection,
    onViewProduct,
    onApproveProduct,
    onRejectProduct,
    adminLoading,
    localLoading,
    tagFilter,
    onTagFilterChange,
  }) => {
    const [productSubTab, setProductSubTab] = React.useState(0);
    const [searchInput, setSearchInput] = React.useState('');
    const [categoryFilter, setCategoryFilter] = React.useState([]);
    const [dateRange, setDateRange] = React.useState('all');

    // Get submission type tag config for pending products
    const getSubmissionTag = (product) => {
      const type = getProductSubmissionType(product);
      const tags = {
        new: { label: 'New', color: '#2196F3', bgColor: '#E3F2FD' },
        edited: { label: 'Edited', color: '#FF9800', bgColor: '#FFF3E0' },
        resubmitted: { label: 'Resubmitted', color: '#9C27B0', bgColor: '#F3E5F5' }
      };
      return tags[type] || tags.new;
    };

    const pendingList = sortedProducts || [];
    const approvedList = approvedProducts || [];
    const rejectedList = rejectedProducts || [];

    // Gather all categories across tabs for filter dropdown
    const allCategories = React.useMemo(() => {
      const set = new Set();
      [...pendingList, ...approvedList, ...rejectedList].forEach(p => {
        if (p.category) set.add(p.category);
      });
      return Array.from(set).sort();
    }, [pendingList, approvedList, rejectedList]);

    const filterByDateRange = React.useCallback((products) => {
      if (!dateRange || dateRange === 'all') return products;
      const now = new Date();
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - days);
      cutoff.setHours(0, 0, 0, 0);
      return products.filter(p => {
        const d = new Date(p.updatedAt || p.createdAt);
        if (isNaN(d.getTime())) return false;
        d.setHours(0, 0, 0, 0);
        return d >= cutoff;
      });
    }, [dateRange]);

    const applyFilters = React.useCallback((products) => {
      let result = products;
      if (searchInput.trim()) {
        const q = searchInput.toLowerCase();
        result = result.filter(p =>
          (p.name && p.name.toLowerCase().includes(q)) ||
          (p.category && p.category.toLowerCase().includes(q)) ||
          (p.sellerName && p.sellerName.toLowerCase().includes(q))
        );
      }
      if (categoryFilter.length > 0) {
        result = result.filter(p => p.category && categoryFilter.includes(p.category));
      }
      result = filterByDateRange(result);
      return result;
    }, [searchInput, categoryFilter, filterByDateRange]);

    // Also apply tag filter to pending products (existing behavior)
    const filteredPending = React.useMemo(() => {
      let products = pendingList;
      if (tagFilter && tagFilter !== 'all') {
        products = products.filter(p => getProductSubmissionType(p) === tagFilter);
      }
      return applyFilters(products);
    }, [pendingList, tagFilter, applyFilters]);

    const filteredApproved = React.useMemo(() => applyFilters(approvedList), [approvedList, applyFilters]);
    const filteredRejected = React.useMemo(() => applyFilters(rejectedList), [rejectedList, applyFilters]);

    const currentProducts = productSubTab === 0 ? filteredPending : productSubTab === 1 ? filteredApproved : filteredRejected;
    const currentLoading = productSubTab === 0 ? pendingProductsLoading : productSubTab === 1 ? approvedProductsLoading : rejectedProductsLoading;

    const statusBadgeColor = (status) =>
      status === 'APPROVED' ? '#10b981' : status === 'REJECTED' ? '#ef4444' : '#f59e0b';

    const renderProductCard = (product) => {
      const imageUrl = getProductPrimaryImageUrl(product);
      const submissionTag = productSubTab === 0 ? getSubmissionTag(product) : null;
      return (
        <Grid item xs={12} sm={6} md={3} key={product.id}>
          <Card
            sx={{
              height: 380,
              display: 'flex',
              flexDirection: 'column',
              border: '2px solid #e2e8f0',
              borderRadius: 0,
              overflow: 'hidden',
              position: 'relative',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                borderColor: '#2C3E50',
              },
            }}
          >
            {/* Image area */}
            <Box sx={{ position: 'relative', width: '100%' }}>
              <Box
                sx={{
                  width: '100%',
                  height: 150,
                  bgcolor: '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  position: 'relative',
                  border: '1px solid #e2e8f0',
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <Box
                  sx={{
                    display: imageUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                    flexDirection: 'column',
                    color: '#94a3b8',
                    bgcolor: '#f1f5f9',
                  }}
                >
                  <PhotoCamera sx={{ fontSize: '2rem', mb: 0.5, opacity: 0.4 }} />
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>No Image Available</Typography>
                </Box>
              </Box>

              {/* Status Badge */}
              <Chip
                label={(product.approvalStatus || 'PENDING').replace('_', ' ').toUpperCase()}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: statusBadgeColor(product.approvalStatus),
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            </Box>

            {/* Card content */}
            <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, '&:last-child': { pb: 2 } }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#1e293b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: 1.3,
                  minHeight: '2.4em',
                }}
              >
                {product.name}
              </Typography>

              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, display: 'block' }}>
                {product.category}
              </Typography>

              {product.sellerName && (
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                  Seller: {product.sellerName}
                </Typography>
              )}

              {/* Submission tag badge for pending products */}
              {submissionTag && (
                <Chip
                  label={submissionTag.label}
                  size="small"
                  sx={{
                    backgroundColor: submissionTag.bgColor,
                    color: submissionTag.color,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 18,
                    width: 'fit-content',
                    mt: 0.5,
                  }}
                />
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto', pt: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#059669', fontWeight: 700 }}>
                  ₹{product.price}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Stock: {product.stockQuantity || 0}
                </Typography>
              </Box>

              {/* Rejection reason for rejected tab */}
              {productSubTab === 2 && product.approvalComments && (
                <Typography variant="caption" sx={{ color: '#b91c1c', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                  Reason: {product.approvalComments}
                </Typography>
              )}

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Button size="small" startIcon={<Visibility />} onClick={() => onViewProduct(product)} sx={{ fontSize: '0.7rem', py: 0.25 }}>
                  View
                </Button>
                {productSubTab === 0 && (
                  <>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => onApproveProduct(product.id)}
                      sx={{ fontSize: '0.7rem', py: 0.25 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => onRejectProduct(product)}
                      sx={{ fontSize: '0.7rem', py: 0.25 }}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      );
    };

    const renderRejectedTable = () => (
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Product</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Seller</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Cancellation Reason</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRejected.map((product) => (
              <TableRow key={product.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      variant="rounded"
                      src={getProductPrimaryImageUrl(product)}
                      alt={product.name}
                      sx={{ width: 56, height: 56, bgcolor: '#f1f5f9', borderRadius: 1 }}
                    >
                      {product.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {product.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>{product.category}</Typography>
                      <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600 }}>₹{product.price}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#475569' }}>{product.sellerName || 'N/A'}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography variant="body2" sx={{ color: '#b91c1c', whiteSpace: 'pre-wrap' }}>
                    {product.approvalComments || 'No reason provided.'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button size="small" startIcon={<Visibility />} onClick={() => onViewProduct(product)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );

    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={productSubTab}
            onChange={(_, v) => setProductSubTab(v)}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, color: '#64748b', fontSize: '0.875rem', '&.Mui-selected': { color: '#3b82f6', fontWeight: 600 } },
              '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: 2 },
            }}
          >
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Pending sx={{ fontSize: '1rem' }} />
                <span>Pending Approval</span>
                <Box sx={{ bgcolor: '#f59e0b', color: 'white', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                  {pendingList.length}
                </Box>
              </Box>
            } />
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle sx={{ fontSize: '1rem' }} />
                <span>Approved</span>
                <Box sx={{ bgcolor: '#10b981', color: 'white', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                  {approvedList.length}
                </Box>
              </Box>
            } />
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Cancel sx={{ fontSize: '1rem' }} />
                <span>Rejected</span>
                <Box sx={{ bgcolor: '#ef4444', color: 'white', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                  {rejectedList.length}
                </Box>
              </Box>
            } />
          </Tabs>

          {/* Filters row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, py: 1.5, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
              {/* Search */}
              <TextField
                variant="outlined"
                placeholder="Search products..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchInput('')} edge="end">
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ minWidth: 200, maxWidth: 280 }}
              />

              {/* Category filter */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  multiple
                  label="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  renderValue={(sel) => sel.length === 0 ? 'All Categories' : sel.length === 1 ? sel[0] : `${sel.length} selected`}
                >
                  {allCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      <Checkbox checked={categoryFilter.includes(cat)} size="small" />
                      <ListItemText primary={cat} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Period filter */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Period</InputLabel>
                <Select label="Period" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                </Select>
              </FormControl>

              {/* Tag filter (pending only) */}
              {productSubTab === 0 && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter by Tag</InputLabel>
                  <Select
                    value={tagFilter || 'all'}
                    label="Filter by Tag"
                    onChange={(e) => onTagFilterChange(e.target.value)}
                  >
                    <MenuItem value="all">All Tags</MenuItem>
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="edited">Edited</MenuItem>
                    <MenuItem value="resubmitted">Resubmitted</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Clear filters */}
              {(searchInput || categoryFilter.length > 0 || dateRange !== 'all') && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterList />}
                  onClick={() => { setSearchInput(''); setCategoryFilter([]); setDateRange('all'); }}
                  sx={{ textTransform: 'none' }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                {currentProducts.length} products found
              </Typography>
              <Button size="small" startIcon={<Refresh />} onClick={onRefresh} disabled={adminLoading || localLoading}>
                Refresh
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        {currentLoading && currentProducts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : currentProducts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Inventory sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
            <Typography variant="h6" color="#64748b">
              {productSubTab === 0 ? 'No products pending approval' : productSubTab === 1 ? 'No approved products' : 'No rejected products'}
            </Typography>
          </Box>
        ) : productSubTab === 2 ? (
          renderRejectedTable()
        ) : (
          <Grid container spacing={2}>
            {currentProducts.map((product) => renderProductCard(product))}
          </Grid>
        )}
      </Box>
    );
  }
);

// Pending Order Approvals Tab Component
const PendingOrderApprovalsTab = React.memo(({ 
  onApproveOrder, 
  onRejectOrder, 
  onViewOrder,
  pendingOrders,
  loading,
  page,
  totalPages,
  totalCount,
  onPageChange,
  onRefresh,
  sortField,
  sortDirection,
  onSort
}) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setViewDialogOpen(true);
  };

  const handleApproveClick = async (order) => {
    try {
      await onApproveOrder(order.id);
      toast.success(`Order ${order.orderNumber} approved successfully!`);
      onRefresh();
    } catch (error) {
      toast.error(error || 'Failed to approve order');
    }
  };

  const handleRejectClick = (order) => {
    setSelectedOrder(order);
    setRejectionReason('');
    setRejectionDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      await onRejectOrder(selectedOrder.id, rejectionReason);
      toast.success(`Order ${selectedOrder.orderNumber} rejected`);
      setRejectionDialogOpen(false);
      setSelectedOrder(null);
      setRejectionReason('');
      onRefresh();
    } catch (error) {
      toast.error(error || 'Failed to reject order');
    }
  };

  const handleSort = (field) => {
    if (onSort) {
      // If clicking on same field, toggle direction; otherwise default to desc
      const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 
                          sortField === field && sortDirection === 'desc' ? 'asc' : 'desc';
      onSort(field, newDirection);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />;
  };

  // Get product names for display
  const getProductNames = (items) => {
    if (!items || items.length === 0) return 'No items';
    if (items.length === 1) return items[0].productName || items[0].name || 'Product';
    if (items.length === 2) {
      return `${items[0].productName || items[0].name || 'Product'} & ${items[1].productName || items[1].name || 'Product'}`;
    }
    return `${items[0].productName || items[0].name || 'Product'} + ${items.length - 1} more`;
  };

  const handleSortByChange = (event) => {
    const newSortField = event.target.value;
    if (onSort) {
      // Default to desc for date, desc for total (newest/highest first)
      const defaultDirection = 'desc';
      onSort(newSortField, defaultDirection);
    }
  };

  const handleSortDirectionChange = (event) => {
    const newDirection = event.target.value;
    if (onSort && sortField) {
      onSort(sortField, newDirection);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        You have {totalCount || 0} orders pending approval. Review and approve or reject each order.
      </Alert>

      {/* Sort Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortField || 'createdAt'}
                label="Sort By"
                onChange={handleSortByChange}
              >
                <MenuItem value="createdAt">Date</MenuItem>
                <MenuItem value="totalAmount">Total</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortDirection || 'desc'}
                label="Order"
                onChange={handleSortDirectionChange}
              >
                <MenuItem value="desc">
                  {sortField === 'createdAt' ? 'Newest First' : 'Highest First'}
                </MenuItem>
                <MenuItem value="asc">
                  {sortField === 'createdAt' ? 'Oldest First' : 'Lowest First'}
                </MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flexGrow: 1 }} />
            <Button 
              startIcon={<Refresh />} 
              onClick={onRefresh} 
              disabled={loading}
              size="small"
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader
          title={`Pending Orders (${totalCount || 0} total)`}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {totalPages > 0 && (
                <Chip
                  label={`Page ${page + 1} of ${totalPages}`}
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          }
        />
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}
                      onClick={() => handleSort('orderNumber')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Order #
                        {getSortIcon('orderNumber')}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Seller</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}
                      onClick={() => handleSort('totalAmount')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Total
                        {getSortIcon('totalAmount')}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}
                      onClick={() => handleSort('createdAt')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Date
                        {getSortIcon('createdAt')}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingOrders && pendingOrders.length > 0 ? (
                    pendingOrders.map((order) => {
                      const sellerNames = order.items ? [...new Set(
                        order.items
                          .map(item => item.sellerName || item.sellerId)
                          .filter(name => name)
                      )] : [];
                      const sellerDisplay = sellerNames.length === 0 
                        ? 'No Seller' 
                        : sellerNames.length === 1 
                        ? sellerNames[0] 
                        : `${sellerNames[0]} +${sellerNames.length - 1} more`;
                      
                      return (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {order.orderNumber || order.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {order.customerName || order.customerId || 'Guest'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: {order.customerId || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {sellerDisplay}
                              </Typography>
                              {order.items && order.items.length > 0 && (
                                <Typography variant="caption" color="textSecondary">
                                  {order.items.length} item(s)
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              ₹{order.totalAmount?.toLocaleString('en-IN') || '0'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <MuiTooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrder(order)}
                                  color="primary"
                                >
                                  <Visibility />
                                </IconButton>
                              </MuiTooltip>
                              <MuiTooltip title="Approve Order">
                                <IconButton
                                  size="small"
                                  onClick={() => handleApproveClick(order)}
                                  color="success"
                                  sx={{ 
                                    bgcolor: 'success.light',
                                    '&:hover': { bgcolor: 'success.main', color: 'white' }
                                  }}
                                >
                                  <CheckCircle />
                                </IconButton>
                              </MuiTooltip>
                              <MuiTooltip title="Reject Order">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRejectClick(order)}
                                  color="error"
                                  sx={{ 
                                    bgcolor: 'error.light',
                                    '&:hover': { bgcolor: 'error.main', color: 'white' }
                                  }}
                                >
                                  <Cancel />
                                </IconButton>
                              </MuiTooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="textSecondary">
                          No pending orders for approval
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(e, value) => onPageChange(value - 1)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Order View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Receipt color="primary" />
            <Typography variant="h6">
              Order Details - {selectedOrder?.orderNumber || selectedOrder?.id}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      fontWeight: 600, 
                      color: '#2C3E50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <Person fontSize="small" />
                      Customer Information
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>ID:</strong> {selectedOrder.customerId || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Name:</strong> {selectedOrder.customerName || 'Guest'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Email:</strong> {selectedOrder.customerEmail || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Phone:</strong> {selectedOrder.customerPhone || 'N/A'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ 
                      fontWeight: 600, 
                      color: '#2C3E50',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <ShoppingCart fontSize="small" />
                      Order Information
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Status:</strong> {' '}
                        <Chip 
                          label={selectedOrder.status || 'PENDING'} 
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Total:</strong> ₹{selectedOrder.totalAmount?.toLocaleString('en-IN') || '0'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Created:</strong> {selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Payment Method:</strong> {selectedOrder.paymentMethod || 'Cash on Delivery'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Payment Status:</strong> {' '}
                        <Chip 
                          size="small" 
                          label={selectedOrder.paymentStatus || 'PENDING'} 
                          color={
                            selectedOrder.paymentStatus === 'PAID' || selectedOrder.paymentStatus === 'COMPLETED' ? 'success' : 
                            selectedOrder.paymentStatus === 'FAILED' ? 'error' : 
                            'warning'
                          }
                          sx={{ fontWeight: 500 }}
                        />
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Order Items */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ 
                        fontWeight: 600, 
                        color: '#2C3E50',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}>
                        <Inventory fontSize="small" />
                        Order Items
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Seller</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedOrder.items.map((item, index) => {
                              // Get product image URL - prefer the order's own snapshot
                              // sources, fall back to the live product reference
                              // (Cloudinary variant if available)
                              const snapshotImageUrl = item.productImageUrl ||
                                                    item.productImage ||
                                                    item.image ||
                                                    item.imageUrl;
                              const fullImageUrl = snapshotImageUrl
                                ? getFullImageUrl(snapshotImageUrl)
                                : getProductPrimaryImageUrl(item.product, false, 'medium');
                              
                              return (
                                <TableRow key={index} sx={{ 
                                  '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                }}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                      {fullImageUrl ? (
                                        <Box
                                          component="img"
                                          src={fullImageUrl}
                                          alt={item.productName || 'Product'}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            if (e.target.nextSibling) {
                                              e.target.nextSibling.style.display = 'flex';
                                            }
                                          }}
                                          sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                          }}
                                        />
                                      ) : null}
                                      <Box sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        bgcolor: 'grey.200', 
                                        borderRadius: 1, 
                                        display: fullImageUrl ? 'none' : 'flex',
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        border: '1px solid',
                                        borderColor: 'divider'
                                      }}>
                                        <Inventory sx={{ color: 'grey.500' }} />
                                      </Box>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                          {item.productName || item.name || item.productId || 'Product'}
                                        </Typography>
                                        {item.category && (
                                          <Chip 
                                            label={item.category}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell>{item.sellerName || item.sellerId || 'N/A'}</TableCell>
                                  <TableCell>{item.quantity || 0}</TableCell>
                                  <TableCell>₹{(item.unitPrice || item.totalPrice / (item.quantity || 1) || 0).toLocaleString('en-IN')}</TableCell>
                                  <TableCell>₹{((item.totalPrice || item.unitPrice * (item.quantity || 1) || 0)).toLocaleString('en-IN')}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
          p: 2
        }}>
          <Button 
            onClick={() => setViewDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Rejection Dialog */}
      <Dialog open={rejectionDialogOpen} onClose={() => setRejectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reject order {selectedOrder?.orderNumber}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this order..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmReject}
            disabled={!rejectionReason.trim()}
          >
            Reject Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

PendingOrderApprovalsTab.displayName = 'PendingOrderApprovalsTab';

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    pendingSellers,
    pendingProducts,
    adminApprovedProducts,
    adminApprovedProductsLoading,
    adminRejectedProducts,
    adminRejectedProductsLoading,
    approvedSellers,
    approvedSellersLoading,
    rejectedSellers,
    rejectedSellersLoading,
    analytics,
    monthlyRevenueFromCompletedOrders,
    pendingSellersLoading,
    pendingProductsLoading,
    analyticsLoading,
    analyticsError,
    monthlyRevenueFromCompletedOrdersLoading,
    loading: adminLoading,
    error
  } = useSelector((state) => state.admin);
  
  // Debug: Log analytics state changes
  useEffect(() => {
    console.log('Analytics state updated:', analytics);
    console.log('Analytics loading:', analyticsLoading);
    console.log('Analytics error:', analyticsError);
  }, [analytics, analyticsLoading, analyticsError]);
  
  const {
    reports,
    reportLoading,
    paymentAnalytics
  } = useSelector((state) => state.analytics);
  
  // Watch for order creation and updates to trigger real-time analytics refresh
  const { createdOrderId, currentOrder, orders } = useSelector((state) => state.orders || {});
  const [lastOrderId, setLastOrderId] = useState(null);
  const [lastOrdersCount, setLastOrdersCount] = useState(0);
  
  const isAdminUser = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  
  const [activeTab, setActiveTab] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  
  // Save sidebar collapsed state to localStorage and dispatch event
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('sidebarStateChanged'));
  }, [sidebarCollapsed]);
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [sellerDialogOpen, setSellerDialogOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [productRejectionDialogOpen, setProductRejectionDialogOpen] = useState(false);
  const [productRejectionTarget, setProductRejectionTarget] = useState(null);
  const [productRejectionReason, setProductRejectionReason] = useState('');
  const [productRejectionError, setProductRejectionError] = useState('');
  const [dateRange, setDateRange] = useState('7d');
  const [localLoading, setLocalLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState({ open: false, src: '', alt: '' });
  
  // New state for button functionalities
  const [pendingApprovalsDialogOpen, setPendingApprovalsDialogOpen] = useState(false);
  const [activeDisputesDialogOpen, setActiveDisputesDialogOpen] = useState(false);
  const [generateReportDialogOpen, setGenerateReportDialogOpen] = useState(false);
  const [securityLogsDialogOpen, setSecurityLogsDialogOpen] = useState(false);
  const [securityLogsLoading, setSecurityLogsLoading] = useState(false);
  const [reportType, setReportType] = useState('sales');
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [securityLogs, setSecurityLogs] = useState([]);
  // Filter states for security logs
  const [logFilterStatus, setLogFilterStatus] = useState('');
  const [logFilterTimeRange, setLogFilterTimeRange] = useState('recent');
  const [logFilterCustomStartDate, setLogFilterCustomStartDate] = useState('');
  const [logFilterCustomEndDate, setLogFilterCustomEndDate] = useState('');
  const [logFilterUserId, setLogFilterUserId] = useState('');
  const [logFilterUserEmail, setLogFilterUserEmail] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [pendingApprovalsTab, setPendingApprovalsTab] = useState('sellers'); // Separate tab state for pending approvals
  
  // Order approval state
  const [pendingOrderApprovals, setPendingOrderApprovals] = useState([]);
  const [pendingOrderApprovalsLoading, setPendingOrderApprovalsLoading] = useState(false);
  const [orderApprovalPage, setOrderApprovalPage] = useState(0);
  const [orderApprovalTotalPages, setOrderApprovalTotalPages] = useState(0);
  const [orderApprovalTotalCount, setOrderApprovalTotalCount] = useState(0);
  const [orderSortField, setOrderSortField] = useState('createdAt');
  const [orderSortDirection, setOrderSortDirection] = useState('desc');
  const [orderRejectionDialogOpen, setOrderRejectionDialogOpen] = useState(false);
  const [selectedOrderForRejection, setSelectedOrderForRejection] = useState(null);
  const [orderRejectionReason, setOrderRejectionReason] = useState('');
  const [orderViewDialogOpen, setOrderViewDialogOpen] = useState(false);
  const [selectedOrderForView, setSelectedOrderForView] = useState(null);
  
  // Sorting state for product approval grid
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Tag filter state for products
  const [tagFilter, setTagFilter] = useState('all');
  
  // Sorting state for pending sellers
  const [sellerSortField, setSellerSortField] = useState('');
  const [sellerSortDirection, setSellerSortDirection] = useState('asc');

  // Seller status sub-tab: 0=Pending, 1=Approved, 2=Rejected
  const [sellerStatusTab, setSellerStatusTab] = useState(0);
  
  // Product categories state
  const [productCategories, setProductCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Top products state
  const [topProducts, setTopProducts] = useState([]);
  const [topProductsLoading, setTopProductsLoading] = useState(false);
  
  // Customer analytics state
  const [userTypeDistribution, setUserTypeDistribution] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [customerAnalyticsLoading, setCustomerAnalyticsLoading] = useState(false);
  const [customerAnalyticsError, setCustomerAnalyticsError] = useState(null);
  
  // Recent activities state
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(false);

  const openImagePreview = useCallback((src, alt = 'Product image preview') => {
    if (!src) return;
    setImagePreview({ open: true, src, alt });
  }, []);

  const closeImagePreview = useCallback(() => {
    setImagePreview({ open: false, src: '', alt: '' });
  }, []);
  
  // Fetch product categories
  const fetchProductCategories = async () => {
    try {
      setCategoriesLoading(true);
      const categories = await productService.getCategories();
      setProductCategories(categories);
    } catch (error) {
      console.error('Error fetching product categories:', error);
      toast.error('Failed to fetch product categories');
    } finally {
      setCategoriesLoading(false);
    }
  };
  
  // Fetch top selling products using sales report data
  const fetchTopProducts = async () => {
    try {
      setTopProductsLoading(true);
      
      console.log('🔍 Fetching top products for current year...');
      
      // Get sales report for current year to extract product data
      const currentYear = new Date().getFullYear();
      const salesReportResponse = await analyticsService.generateSalesReport({
        period: 'yearly',
        year: currentYear
      });
      
      console.log('📊 Sales Report Response:', salesReportResponse);
      
      // Extract the data object from the response (backend returns { data: {...}, reportType, period, year })
      const salesData = salesReportResponse?.data || salesReportResponse;
      
      console.log('📊 Sales Data:', salesData);
      console.log('📦 Top Products from backend:', salesData?.topProducts);
      
      // First, try to use the topProducts data from backend
      if (salesData?.topProducts && salesData.topProducts.length > 0) {
        console.log('✅ Using real top products data from backend');
        // Use real data from backend
        const realTopProducts = salesData.topProducts.map(product => ({
          id: product.productId,
          name: product.productName,
          quantitySold: product.quantitySold,
          totalOrders: product.totalOrders,
          revenue: product.revenue,
          category: product.category
        }));
        setTopProducts(realTopProducts);
        console.log('✅ Set top products:', realTopProducts);
        return;
      }
      
      console.log('⚠️ No topProducts from backend, trying to extract from recentOrders');
      
      // Fallback: Extract product data from order items in the sales report
      const productMap = new Map();
      
      if (salesData?.recentOrders) {
        console.log('📋 Processing recent orders:', salesData.recentOrders.length);
        salesData.recentOrders.forEach(order => {
          if (order.items) {
            order.items.forEach(item => {
              const productId = item.productId;
              const productName = item.productName || item.name || 'Unknown Product';
              
              if (productMap.has(productId)) {
                const existing = productMap.get(productId);
                existing.quantitySold += item.quantity || 1;
                existing.totalOrders += 1;
                existing.revenue += (item.unitPrice || 0) * (item.quantity || 1);
              } else {
                productMap.set(productId, {
                  id: productId,
                  name: productName,
                  quantitySold: item.quantity || 1,
                  totalOrders: 1,
                  revenue: (item.unitPrice || 0) * (item.quantity || 1),
                  category: item.category || 'General'
                });
              }
            });
          }
        });
      }
      
      // Convert to array and sort by total orders (most ordered products)
      const topProductsArray = Array.from(productMap.values())
        .sort((a, b) => b.totalOrders - a.totalOrders)
        .slice(0, 10);
      
      console.log('📊 Extracted products from orders:', topProductsArray);
      
      // Only set products if we have real data
      if (topProductsArray.length > 0) {
        console.log('✅ Using extracted products from orders');
        setTopProducts(topProductsArray);
      } else {
        console.log('❌ No product data found - setting empty array');
        setTopProducts([]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching top products:', error);
      setTopProducts([]);
      toast.error('Failed to load top products data');
    } finally {
      setTopProductsLoading(false);
    }
  };
  
  // Fetch customer analytics data
  const fetchCustomerAnalytics = useCallback(async () => {
    try {
      setCustomerAnalyticsLoading(true);
      setCustomerAnalyticsError(null);
      
      // Fetch user type distribution and top customers in parallel
      const [userDistributionResponse, topCustomersResponse] = await Promise.all([
        analyticsService.getUserTypeDistribution(),
        analyticsService.getTopCustomers()
      ]);
      
      console.log('User Distribution Response:', userDistributionResponse);
      console.log('Top Customers Response:', topCustomersResponse);
      
      // Extract the actual data from the response objects
      const userDistributionData = userDistributionResponse?.userTypeDistribution || userDistributionResponse?.data?.userTypeDistribution || null;
      const topCustomersData = topCustomersResponse?.topCustomers || topCustomersResponse?.data?.topCustomers || [];
      
      console.log('Extracted User Distribution Data:', userDistributionData);
      console.log('Extracted Top Customers Data (RAW):', topCustomersData);
      console.log('Number of customers received:', topCustomersData?.length || 0);
      
      // Log each customer for debugging
      if (Array.isArray(topCustomersData) && topCustomersData.length > 0) {
        console.log('Customer details:');
        topCustomersData.forEach((customer, index) => {
          console.log(`  Customer ${index + 1}:`, {
            id: customer.id,
            name: customer.name || customer.customerName,
            totalOrders: customer.totalOrders || customer.orders,
            totalPurchase: customer.totalPurchase || customer.totalSpent || customer.revenue
          });
        });
      }
      
      setUserTypeDistribution(userDistributionData);
      setTopCustomers(topCustomersData);
      
    } catch (error) {
      console.error('Error fetching customer analytics:', error);
      setCustomerAnalyticsError(error.message || 'Failed to load customer analytics data');
      toast.error('Failed to fetch customer analytics data');
      
      // Set empty data on error
      setUserTypeDistribution(null);
      setTopCustomers([]);
    } finally {
      setCustomerAnalyticsLoading(false);
    }
  }, []);
  
  // Helper functions to get array data from potentially paginated responses
  const getPendingSellersArray = useCallback(() => {
    if (Array.isArray(pendingSellers)) return pendingSellers;
    if (pendingSellers?.content && Array.isArray(pendingSellers.content)) return pendingSellers.content;
    return [];
  }, [pendingSellers]);

  const getApprovedSellersArray = useCallback(() => {
    if (Array.isArray(approvedSellers)) return approvedSellers;
    if (approvedSellers?.content && Array.isArray(approvedSellers.content)) return approvedSellers.content;
    return [];
  }, [approvedSellers]);

  const getRejectedSellersArray = useCallback(() => {
    if (Array.isArray(rejectedSellers)) return rejectedSellers;
    if (rejectedSellers?.content && Array.isArray(rejectedSellers.content)) return rejectedSellers.content;
    return [];
  }, [rejectedSellers]);

  const getPendingProductsArray = useCallback(() => {
    if (Array.isArray(pendingProducts)) return pendingProducts;
    if (pendingProducts?.content && Array.isArray(pendingProducts.content)) return pendingProducts.content;
    return [];
  }, [pendingProducts]);
  
  // Fetch recent activities - defined early so it can be used in useEffect
  const fetchRecentActivities = useCallback(async () => {
    if (!isAdminUser) return;
    
    try {
      setRecentActivitiesLoading(true);
      
      // Fetch recent orders (last 10)
      const recentOrdersResponse = await orderService.getAllOrdersForAdmin({
        page: 0,
        size: 10,
        sortBy: 'createdAt',
        sortDir: 'desc'
      });
      
      const recentOrders = recentOrdersResponse?.orders || [];
      const pendingProductsArray = getPendingProductsArray();
      const pendingSellersArray = getPendingSellersArray();
      
      // Combine all activities
      const activities = [];
      
      // Add recent orders
      recentOrders.forEach(order => {
        activities.push({
          id: `order-${order.id}`,
          type: 'order',
          title: `New Order #${order.orderNumber || order.id?.substring(0, 8)}`,
          description: `Order placed by ${order.customerName || 'Customer'} - ₹${order.totalAmount?.toLocaleString('en-IN') || '0'}`,
          timestamp: order.createdAt || order.orderDate,
          icon: <ShoppingCart />,
          color: 'primary',
          status: order.status
        });
      });
      
      // Add recent product submissions (last 10)
      pendingProductsArray.slice(0, 10).forEach(product => {
        const submissionType = getProductSubmissionType(product);
        const typeLabel = submissionType === 'new' ? 'New Product' : 
                         submissionType === 'edited' ? 'Product Edited' : 
                         'Product Resubmitted';
        
        activities.push({
          id: `product-${product.id}`,
          type: 'product',
          title: typeLabel,
          description: `${product.name} submitted by ${product.sellerName || 'Seller'}`,
          timestamp: product.createdAt || product.updatedAt,
          icon: <Inventory />,
          color: submissionType === 'new' ? 'info' : submissionType === 'edited' ? 'warning' : 'secondary',
          status: 'PENDING'
        });
      });
      
      // Add recent seller submissions (last 10)
      pendingSellersArray.slice(0, 10).forEach(seller => {
        activities.push({
          id: `seller-${seller.id}`,
          type: 'seller',
          title: 'New Seller Application',
          description: `${seller.businessProfile?.businessName || `${seller.firstName} ${seller.lastName}`} applied for seller account`,
          timestamp: seller.createdAt || seller.registrationDate,
          icon: <People />,
          color: 'success',
          status: 'PENDING'
        });
      });
      
      // Sort by timestamp (most recent first) and limit to 15
      const sortedActivities = activities
        .filter(activity => activity.timestamp)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 15);
      
      setRecentActivities(sortedActivities);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      setRecentActivities([]);
    } finally {
      setRecentActivitiesLoading(false);
    }
  }, [isAdminUser, getPendingProductsArray, getPendingSellersArray]);

  // Function to refresh all analytics data
  const refreshAnalyticsData = useCallback(() => {
    if (isAdminUser) {
      dispatch(fetchAdminAnalytics());
      dispatch(fetchMonthlyRevenueFromCompletedOrders(new Date().getFullYear()));
      dispatch(fetchPaymentAnalytics()); // Fetch payment analytics for pie chart
      fetchProductCategories(); // Fetch real product categories
      fetchTopProducts(); // Fetch real top products data
      fetchCustomerAnalytics(); // Fetch customer analytics data
      
      // Generate sales report for Top 10 Products Chart
      dispatch(generateSalesReport({
        period: 'monthly',
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 30 days
        endDate: new Date().toISOString().split('T')[0]
      }));
      
      // Fetch user analytics for Customer Reports
      dispatch(fetchUserAnalytics());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAdminUser]);

  // Track last refresh time to prevent excessive refreshes
  const lastRefreshTimeRef = React.useRef(0);
  const lastActivitiesRefreshTimeRef = React.useRef(0);
  const REFRESH_COOLDOWN = 30000; // 30 seconds minimum between refreshes
  const ACTIVITIES_REFRESH_COOLDOWN = 60000; // 60 seconds minimum between activity refreshes

  // Fetch data on component mount - only if user is admin
  useEffect(() => {
    if (isAdminUser) {
      dispatch(fetchPendingSellers());
      dispatch(fetchPendingProducts({ fetchAll: true }));
      refreshAnalyticsData();
      // Fetch recent activities after a short delay to ensure pending data is loaded
      setTimeout(() => {
        fetchRecentActivities();
        lastActivitiesRefreshTimeRef.current = Date.now();
      }, 1000);
      lastRefreshTimeRef.current = Date.now();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAdminUser, user?.id, user?.role]);
  
  // Fetch approved and rejected products when admin products tab becomes active
  useEffect(() => {
    if (isAdminUser && activeTab === 2) {
      dispatch(fetchAdminApprovedProducts());
      dispatch(fetchAdminRejectedProducts());
    }
  }, [activeTab, isAdminUser, dispatch]);

  // Fetch approved and rejected sellers when Sellers tab becomes active
  useEffect(() => {
    if (isAdminUser && activeTab === 1) {
      dispatch(fetchApprovedSellers());
      dispatch(fetchRejectedSellers());
    }
  }, [activeTab, isAdminUser, dispatch]);

  // Refresh recent activities when pending data changes - with debounce and cooldown
  useEffect(() => {
    if (!isAdminUser || (!pendingSellers && !pendingProducts)) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastRefresh = now - lastActivitiesRefreshTimeRef.current;
    
    // Only refresh if enough time has passed since last refresh
    if (timeSinceLastRefresh < ACTIVITIES_REFRESH_COOLDOWN) {
      return;
    }
    
    // Debounce the refresh
    const timeoutId = setTimeout(() => {
      fetchRecentActivities();
      lastActivitiesRefreshTimeRef.current = Date.now();
    }, 2000); // 2 second debounce
    
    return () => clearTimeout(timeoutId);
  }, [isAdminUser, pendingSellers, pendingProducts]);

  // Refresh analytics data when switching to Overview or Analytics tabs - with cooldown
  useEffect(() => {
    if (!isAdminUser || (activeTab !== 0 && activeTab !== 8)) {
      return;
    }
    
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
    
    // Only refresh if enough time has passed since last refresh (prevent rapid tab switching from causing refreshes)
    if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
      return;
    }
    
    refreshAnalyticsData();
    lastRefreshTimeRef.current = Date.now();
    
    // Also refresh customer analytics specifically when opening Analytics tab
    if (activeTab === 8) {
      fetchCustomerAnalytics();
    }
  }, [activeTab, isAdminUser]);

  // Real-time refresh: Watch for order creation/updates and refresh analytics - with cooldown
  useEffect(() => {
    if (!isAdminUser) return;
    
    // Check if a new order was created
    if (createdOrderId && createdOrderId !== lastOrderId) {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
      
      // Only refresh if enough time has passed since last refresh
      if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
        setLastOrderId(createdOrderId); // Update the ID but skip refresh
        return;
      }
      
      console.log('🔄 New order detected! Refreshing analytics...', createdOrderId);
      setLastOrderId(createdOrderId);
      
      // Always refresh customer analytics when new order is created
      // Small delay to ensure backend has processed the order
      setTimeout(() => {
        fetchCustomerAnalytics();
        // Also refresh full analytics if on Overview or Analytics tabs
        if (activeTab === 0 || activeTab === 8) {
          refreshAnalyticsData();
          lastRefreshTimeRef.current = Date.now();
        }
      }, 2000); // Increased delay to 2 seconds
    }
  }, [createdOrderId, lastOrderId, isAdminUser, activeTab]);

  // Watch for changes in orders list (e.g., when orders are fetched/updated from admin page) - with cooldown
  useEffect(() => {
    if (!isAdminUser) return;
    
    const ordersArray = Array.isArray(orders) ? orders : [];
    const currentOrdersCount = ordersArray.length;
    
    // Check if orders list changed (new orders added or order statuses updated)
    if (currentOrdersCount !== lastOrdersCount && lastOrdersCount > 0) {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
      
      // Only refresh if enough time has passed since last refresh
      if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
        setLastOrdersCount(currentOrdersCount); // Update the count but skip refresh
        return;
      }
      
      console.log('🔄 Orders list changed! Refreshing analytics...', {
        previous: lastOrdersCount,
        current: currentOrdersCount
      });
      setLastOrdersCount(currentOrdersCount);
      
      // Always refresh customer analytics when orders change
      // Debounce to avoid multiple refreshes
      const timeoutId = setTimeout(() => {
        fetchCustomerAnalytics();
        // Also refresh full analytics if on Overview or Analytics tabs
        if (activeTab === 0 || activeTab === 8) {
          refreshAnalyticsData();
          lastRefreshTimeRef.current = Date.now();
        }
      }, 3000); // Increased debounce to 3 seconds
      
      return () => clearTimeout(timeoutId);
    } else if (lastOrdersCount === 0 && currentOrdersCount > 0) {
      // Initial load - just update the count
      setLastOrdersCount(currentOrdersCount);
    }
  }, [orders, lastOrdersCount, isAdminUser, activeTab]);

  // Watch for order status updates (completed, shipped, etc.) - with cooldown
  useEffect(() => {
    if (!isAdminUser || !currentOrder) return;
    
    // Check if order status changed to a significant state
    const significantStatuses = ['COMPLETED', 'DELIVERED', 'SHIPPED', 'PROCESSING'];
    if (currentOrder.status && significantStatuses.includes(currentOrder.status)) {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
      
      // Only refresh if enough time has passed since last refresh
      if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
        return;
      }
      
      console.log('🔄 Order status updated! Refreshing analytics...', currentOrder.status);
      // Always refresh customer analytics when order status changes
      // Debounce to avoid multiple refreshes
      const timeoutId = setTimeout(() => {
        fetchCustomerAnalytics();
        // Also refresh full analytics if on Overview or Analytics tabs
        if (activeTab === 0 || activeTab === 8) {
          refreshAnalyticsData();
          lastRefreshTimeRef.current = Date.now();
        }
      }, 2000); // Increased debounce to 2 seconds
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentOrder?.status, currentOrder?.id, isAdminUser, activeTab]);

  // Auto-refresh analytics data every 5 minutes when on Overview or Analytics tabs
  useEffect(() => {
    if (!isAdminUser || (activeTab !== 0 && activeTab !== 8)) {
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTimeRef.current;
      
      // Only refresh if enough time has passed (respect cooldown)
      if (timeSinceLastRefresh >= REFRESH_COOLDOWN) {
        refreshAnalyticsData();
        lastRefreshTimeRef.current = Date.now();
        
        // Also refresh customer analytics when on Analytics tab
        if (activeTab === 8) {
          fetchCustomerAnalytics();
        }
      }
    }, 300000); // Refresh every 5 minutes (300000ms) instead of 30 seconds

    return () => clearInterval(interval);
  }, [activeTab, isAdminUser]);
  
  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearAdminError());
    };
  }, [dispatch]);
  
  // Memoized values - MUST be before any conditional returns (Rules of Hooks)
  const sidebarItems = useMemo(() => [
    { label: 'Overview', value: 0, icon: <DashboardIcon /> },
    { label: 'Sellers', value: 1, icon: <People /> },
    { label: 'Products', value: 2, icon: <Inventory /> },
    { label: 'Trending Products', value: 3, icon: <Star /> },
    { label: 'Orders', value: 4, icon: <ShoppingCart /> },
    { label: 'Pending Orders', value: 10, icon: <Pending /> },
    { label: 'Visit Requests', value: 5, icon: <HomeWork /> },
    { label: 'Lot Requests', value: 6, icon: <Assignment /> },
    { label: 'Financial', value: 7, icon: <AttachMoney /> },
    { label: 'Analytics', value: 8, icon: <Analytics /> },
    { label: 'Settings', value: 9, icon: <Settings /> },
  ], []);

  const pendingProductsArray = useMemo(() => getPendingProductsArray(), [getPendingProductsArray]);

  const pendingProductsCount = useMemo(() => {
    if (typeof pendingProducts?.totalElements === 'number') return pendingProducts.totalElements;
    if (Array.isArray(pendingProducts?.content)) return pendingProducts.content.length;
    if (Array.isArray(pendingProductsArray)) return pendingProductsArray.length;
    return 0;
  }, [pendingProducts, pendingProductsArray]);
  
  // Enhanced dashboard stats with real data from analytics
  const dashboardStats = useMemo(() => {
    // Debug logging
    console.log('Dashboard Stats Analytics:', analytics);
    console.log('Total Users:', analytics?.totalUsers, analytics?.users?.totalUsers);
    console.log('Active Sellers:', analytics?.activeSellers, analytics?.totalSellers, analytics?.users?.activeSellers);
    console.log('Total Products:', analytics?.totalProducts, analytics?.users?.totalProducts);
    
    return {
      totalUsers: { 
        value: analytics?.totalUsers || analytics?.users?.totalUsers || 0, 
        trend: analytics?.userGrowthRate || 12.5, 
        period: 'vs last month' 
      },
      totalSellers: { 
        value: analytics?.activeSellers || analytics?.totalSellers || analytics?.users?.activeSellers || analytics?.users?.totalSellers || 0, 
        trend: analytics?.sellerGrowthRate || 8.3, 
        period: 'vs last month' 
      },
      totalProducts: { 
        value: analytics?.totalProducts || analytics?.users?.totalProducts || 0, 
        trend: analytics?.productGrowthRate || 15.2, 
        period: 'vs last month' 
      },
      totalOrders: { 
        value: analytics?.totalOrders || analytics?.orders?.totalOrders || 0, 
        trend: analytics?.orderGrowthRate || 22.1, 
        period: 'vs last month' 
      },
      totalRevenue: { 
        value: analytics?.totalRevenue || analytics?.payments?.totalRevenue || 0, 
        trend: analytics?.revenueGrowthRate || 18.7, 
        period: 'vs last month' 
      },
      pendingApprovals: { 
        value: getPendingSellersArray().length + getPendingProductsArray().length, 
        trend: analytics?.approvalTrend || -5.5, 
        period: 'vs last week' 
      },
      activeDisputes: { 
        value: analytics?.activeDisputes || analytics?.orders?.activeDisputes || 0, 
        trend: analytics?.disputeTrend || -2.1, 
        period: 'vs last week' 
      },
      monthlyGrowth: analytics?.monthlyGrowth || analytics?.payments?.monthlyGrowth || 0,
      conversionRate: { 
        value: analytics?.conversionRate || analytics?.orders?.conversionRate || 0, 
        trend: analytics?.conversionTrend || 0.8, 
        period: 'vs last month' 
      },
      avgOrderValue: { 
        value: analytics?.avgOrderValue || analytics?.payments?.avgOrderValue || 0, 
        trend: analytics?.avgOrderValueTrend || 5.4, 
        period: 'vs last month' 
      }
    };
  }, [analytics, getPendingSellersArray, getPendingProductsArray]);
  
  const monthOrder = useMemo(() => ([
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]), []);
  
const revenueData = useMemo(() => {
  const monthIndexMap = monthOrder.reduce((acc, month, index) => {
    acc[month] = index;
    return acc;
  }, {});

  const normaliseMonth = (entry, fallbackIndex = 0) => {
    const rawMonth =
      entry.month ??
      entry.monthName ??
      entry.name ??
      entry.label ??
      (typeof entry.monthIndex === 'number' ? monthOrder[entry.monthIndex] : undefined);

    if (typeof rawMonth === 'string') {
      const trimmed = rawMonth.trim();
      const match = monthOrder.find(
        (month) => month.toLowerCase() === trimmed.slice(0, 3).toLowerCase()
      );
      if (match) {
        return match;
      }
    }

    const numericMonth =
      typeof entry.month === 'number'
        ? entry.month
        : typeof entry.monthNumber === 'number'
        ? entry.monthNumber
        : typeof entry.monthIndex === 'number'
        ? entry.monthIndex + 1
        : typeof entry.index === 'number'
        ? entry.index + 1
        : fallbackIndex + 1;

    const clampedIndex = Math.max(0, Math.min(11, Number(numericMonth || 1) - 1));
    return monthOrder[clampedIndex] ?? monthOrder[0];
  };

  const normaliseEntry = (entry, fallbackIndex = 0) => {
    const month = normaliseMonth(entry, fallbackIndex);
    const revenue = Number(
      entry.revenue ??
        entry.totalRevenue ??
        entry.amount ??
        entry.value ??
        entry.gross ??
        0
    );
    const orders = Number(
      entry.orders ??
        entry.totalOrders ??
        entry.count ??
        entry.orderCount ??
        entry.quantity ??
        0
    );

    return { month, revenue, orders };
  };

  const fromCompletedOrders = Array.isArray(monthlyRevenueFromCompletedOrders?.monthlyRevenue)
    ? monthlyRevenueFromCompletedOrders.monthlyRevenue.map((entry, index) =>
        normaliseEntry(entry, index)
      )
    : [];

  const extractSalesReportBreakdown = (report) => {
    if (!report) return [];
    const rawBreakdown =
      report.monthlyBreakdown && typeof report.monthlyBreakdown === 'object'
        ? report.monthlyBreakdown
        : report.data?.monthlyBreakdown && typeof report.data.monthlyBreakdown === 'object'
        ? report.data.monthlyBreakdown
        : null;

    if (!rawBreakdown) return [];

    return Object.values(rawBreakdown).map((entry, index) => normaliseEntry(entry, index));
  };

  const fromSalesReport =
    fromCompletedOrders.length === 0 ? extractSalesReportBreakdown(reports?.sales) : [];

  const fromAnalytics =
    fromCompletedOrders.length === 0 && fromSalesReport.length === 0
      ? Array.isArray(analytics?.monthlyRevenue)
        ? analytics.monthlyRevenue.map((entry, index) => normaliseEntry(entry, index))
        : Array.isArray(analytics?.payments?.monthlyRevenue)
        ? analytics.payments.monthlyRevenue.map((entry, index) =>
            normaliseEntry(entry, index)
          )
        : []
      : [];

  const combined = [...fromCompletedOrders, ...fromSalesReport, ...fromAnalytics].filter(
    (item) => item.month
  );

  if (!combined.length) {
    return [];
  }

  const mergedByMonth = combined.reduce((acc, item) => {
    if (!acc.has(item.month)) {
      acc.set(item.month, { ...item });
    } else {
      const existing = acc.get(item.month);
      acc.set(item.month, {
        month: item.month,
        revenue: Number(existing.revenue || 0) + Number(item.revenue || 0),
        orders: Number(existing.orders || 0) + Number(item.orders || 0),
      });
    }
    return acc;
  }, new Map());

  return Array.from(mergedByMonth.values()).sort((a, b) => {
    const aIndex = monthIndexMap[a.month] ?? 99;
    const bIndex = monthIndexMap[b.month] ?? 99;
    return aIndex - bIndex;
  });
}, [
  analytics?.monthlyRevenue,
  analytics?.payments?.monthlyRevenue,
  monthOrder,
  monthlyRevenueFromCompletedOrders?.monthlyRevenue,
  reports?.sales,
]);
  
  const revenueSummary = useMemo(() => {
    if (!revenueData.length) {
      return { total: 0, average: 0, latest: 0, latestMonth: null };
    }
    
    const totals = revenueData.reduce(
      (acc, item) => {
        acc.total += item.revenue;
        acc.orders += item.orders;
        return acc;
      },
      { total: 0, orders: 0 }
    );
    
    const latestEntry = revenueData[revenueData.length - 1];
    
    return {
      total: totals.total,
      orders: totals.orders,
      average: totals.total / revenueData.length || 0,
      latest: latestEntry.revenue,
      latestMonth: latestEntry.month,
    };
  }, [revenueData]);

  const formatCurrency = useCallback(
    (amount) =>
      `₹${Number(amount || 0).toLocaleString('en-IN', {
        maximumFractionDigits: 0,
      })}`,
    []
  );

  const formatCompactCurrency = useCallback((value) => {
    const amount = Number(value || 0);
    if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(1)} Cr`;
    if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(1)} L`;
    if (amount >= 1e3) return `₹${(amount / 1e3).toFixed(0)} K`;
    return `₹${Math.round(amount)}`;
  }, []);
  
  const salesPerformanceData = useMemo(
    () =>
      revenueData.map((item) => ({
        month: item.month,
        sales: item.revenue,
        orders: item.orders,
      })),
    [revenueData]
  );
  
  const topCustomersData = useMemo(() => {
    console.log('🔄 Processing topCustomersData - Input:', topCustomers);
    console.log('🔄 topCustomers is array?', Array.isArray(topCustomers));
    console.log('🔄 topCustomers length:', topCustomers?.length || 0);
    
    if (!Array.isArray(topCustomers) || topCustomers.length === 0) {
      console.log('⚠️ No customers data available');
      return [];
    }
    
    // Sort by totalPurchase descending and return all customers (not limited)
    const sortedCustomers = [...topCustomers].sort((a, b) => {
      const aPurchase = Number(a.totalPurchase ?? a.totalSpent ?? a.revenue ?? a.totalValue ?? 0) || 0;
      const bPurchase = Number(b.totalPurchase ?? b.totalSpent ?? b.revenue ?? b.totalValue ?? 0) || 0;
      return bPurchase - aPurchase;
    });
    
    console.log('🔄 Sorted customers count:', sortedCustomers.length);
    
    const processedCustomers = sortedCustomers.map((customer, index) => {
      const processed = {
        id: customer.id || customer.customerId || `customer-${index}`,
        name: customer.name || customer.customerName || customer.fullName || `Customer ${index + 1}`,
        totalPurchase:
          Number(customer.totalPurchase ?? customer.totalSpent ?? customer.revenue ?? customer.totalValue ?? 0) || 0,
        orders: Number(customer.totalOrders ?? customer.orders ?? customer.orderCount ?? 0) || 0,
        avgOrder:
          Number(
            customer.avgOrderValue ??
              customer.averageOrderValue ??
              (customer.totalPurchase && (customer.totalOrders || customer.orders)
                ? customer.totalPurchase / (customer.totalOrders || customer.orders || 1)
                : 0)
          ) || 0,
        category: customer.category || customer.segment || customer.type || 'Customer',
      };
      console.log(`  Processed Customer ${index + 1}:`, processed.name, 'Orders:', processed.orders, 'Purchase:', processed.totalPurchase);
      return processed;
    });
    
    console.log('✅ Final topCustomersData count:', processedCustomers.length);
    return processedCustomers;
  }, [topCustomers]);
  
  const topCustomerHighlightMetrics = useMemo(() => {
    if (!topCustomersData.length) {
      return [];
    }
    const leadingCustomer = topCustomersData[0];
    return [
      { label: 'Top Customer', value: leadingCustomer?.name || '—' },
      { label: 'Total Purchase', value: formatCurrency(leadingCustomer?.totalPurchase || 0) },
      { label: 'Orders', value: (leadingCustomer?.orders ?? 0).toLocaleString('en-IN') },
    ];
  }, [topCustomersData, formatCurrency]);
  
  const categoryData = useMemo(() => {
    const raw = analytics?.categoryBreakdown && analytics.categoryBreakdown.length > 0
      ? analytics.categoryBreakdown
      : productCategories;
    
    if (!raw || raw.length === 0) {
      return [];
    }
    
    const totalCount = raw.reduce((sum, category) => sum + Number(category.count ?? category.value ?? 0), 0);
    
    return raw
      .map((category, index) => {
        const count = Number(category.count ?? category.value ?? 0);
        const revenue = Number(category.revenue ?? category.sales ?? 0);
        // Use vibrant palette colors directly, ensuring full opacity
        const vibrantColor = CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length];
        
        return {
          name: category.name || category.category || category.label || `Category ${index + 1}`,
          count,
          revenue,
          percentage: totalCount > 0 ? (count / totalCount) * 100 : 0,
          color: vibrantColor,
          description: category.description,
        };
      })
      .filter((category) => category.count > 0);
  }, [analytics?.categoryBreakdown, productCategories]);

  const renderCategoryLabel = useCallback(({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    if (percent === 0) {
      return null;
    }
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + radius * Math.cos(-midAngle * CATEGORY_LABEL_RADIAN);
    const y = cy + radius * Math.sin(-midAngle * CATEGORY_LABEL_RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#0F172A"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  }, []);
  
  // Mock data removed - now using Redux state
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleApproveSeller = async (seller) => {
    try {
      setLocalLoading(true);
      await dispatch(approveSeller(seller.id)).unwrap();
      toast.success(`Seller ${seller.businessProfile?.businessName || seller.firstName + ' ' + seller.lastName} approved successfully!`);
      dispatch(fetchPendingSellers());
      dispatch(fetchApprovedSellers());
      dispatch(fetchRejectedSellers());
    } catch (error) {
      toast.error(error || 'Failed to approve seller');
    } finally {
      setLocalLoading(false);
    }
  };
  
  const handleRejectSeller = (seller) => {
    setSelectedSeller(seller);
    setRejectionDialogOpen(true);
  };
  
  const handleConfirmRejectSeller = async () => {
    if (!selectedSeller || !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    try {
      setLocalLoading(true);
      await dispatch(rejectSeller({ 
        sellerId: selectedSeller.id, 
        reason: rejectionReason 
      })).unwrap();
      toast.success(`Seller ${selectedSeller.businessProfile?.businessName || selectedSeller.firstName + ' ' + selectedSeller.lastName} rejected successfully!`);
      dispatch(fetchPendingSellers());
      dispatch(fetchRejectedSellers());
      setRejectionDialogOpen(false);
      setRejectionReason('');
      setSelectedSeller(null);
    } catch (error) {
      toast.error(error || 'Failed to reject seller');
    } finally {
      setLocalLoading(false);
    }
  };
  
  const confirmApproval = async () => {
    try {
      await dispatch(approveSeller(selectedSeller.id)).unwrap();
      toast.success(`Seller ${selectedSeller.name} approved successfully!`);
      setApprovalDialogOpen(false);
      setSelectedSeller(null);
    } catch (error) {
      toast.error(error || 'Failed to approve seller');
    }
  };
  
  const confirmRejection = async () => {
    if (rejectionReason.trim()) {
      try {
        await dispatch(rejectSeller({ 
          sellerId: selectedSeller.id, 
          reason: rejectionReason 
        })).unwrap();
        toast.success(`Seller ${selectedSeller.name} rejected.`);
        setRejectionDialogOpen(false);
        setSelectedSeller(null);
        setRejectionReason('');
      } catch (error) {
        toast.error(error || 'Failed to reject seller');
      }
    } else {
      toast.error('Please provide a reason for rejection.');
    }
  };
  
  const handleApproveProduct = useCallback(async (productId) => {
    try {
      await dispatch(approveProduct(productId)).unwrap();
      toast.success('Product approved successfully!');
      dispatch(fetchPendingProducts({ fetchAll: true }));
      dispatch(fetchAdminApprovedProducts());
    } catch (error) {
      toast.error(error || 'Failed to approve product');
    }
  }, [dispatch]);

  const handleRejectProduct = async (productId, reason) => {
    const trimmedReason = reason?.trim();
    if (!trimmedReason) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    try {
      setLocalLoading(true);
      await dispatch(rejectProduct({ productId, reason: trimmedReason })).unwrap();
      toast.success('Product rejected successfully');
      dispatch(fetchAdminRejectedProducts());
    } catch (error) {
      toast.error(error || 'Failed to reject product');
    } finally {
      setLocalLoading(false);
    }
  };

  const openProductRejectionDialog = useCallback((product) => {
    if (!product) return;
    setProductRejectionTarget(product);
    setProductRejectionReason('');
    setProductRejectionError('');
    setProductRejectionDialogOpen(true);
  }, []);

  const closeProductRejectionDialog = useCallback(() => {
    setProductRejectionDialogOpen(false);
    setProductRejectionTarget(null);
    setProductRejectionReason('');
    setProductRejectionError('');
  }, []);

  const handleViewProduct = useCallback((product) => {
    setSelectedProduct(product);
    setProductDialogOpen(true);
  }, []);

  const confirmProductRejection = async () => {
    if (!productRejectionReason.trim()) {
      setProductRejectionError('Please provide a rejection reason.');
      return;
    }
    if (!productRejectionTarget) {
      toast.error('No product selected for rejection');
      return;
    }
    await handleRejectProduct(productRejectionTarget.id, productRejectionReason);
    closeProductRejectionDialog();
  };
  
  const handleRefreshData = useCallback(() => {
    dispatch(fetchPendingSellers());
    dispatch(fetchApprovedSellers());
    dispatch(fetchRejectedSellers());
    dispatch(fetchPendingProducts({ fetchAll: true })).then(() => {
      // Force a re-render by updating local state
      setLocalLoading(true);
      setTimeout(() => {
        setLocalLoading(false);
      }, 100);
    });
    dispatch(fetchAdminApprovedProducts());
    dispatch(fetchAdminRejectedProducts());
    dispatch(fetchAdminAnalytics());
    fetchCustomerAnalytics(); // Refresh customer analytics data
    loadPendingOrderApprovals(); // Refresh pending order approvals
    toast.info('Data refreshed!');
  }, [dispatch, fetchCustomerAnalytics]);

  // Load pending order approvals
  const loadPendingOrderApprovals = useCallback(async () => {
    try {
      setPendingOrderApprovalsLoading(true);
      // Pass sort parameters to backend for proper sorting across all pages
      const response = await orderService.getPendingApprovalOrders({
        page: orderApprovalPage,
        size: 20,
        sortBy: orderSortField || 'createdAt',
        sortDir: orderSortDirection || 'desc'
      });
      let orders = response.orders || [];
      
      // Backend should handle sorting, but apply frontend sorting as fallback
      // This ensures consistent sorting even if backend doesn't support it
      orders = [...orders].sort((a, b) => {
        let aValue, bValue;
        switch (orderSortField) {
          case 'orderNumber':
            aValue = a.orderNumber || '';
            bValue = b.orderNumber || '';
            break;
          case 'customerName':
            aValue = (a.customerName || '').toLowerCase();
            bValue = (b.customerName || '').toLowerCase();
            break;
          case 'totalAmount':
            aValue = a.totalAmount || 0;
            bValue = b.totalAmount || 0;
            break;
          case 'createdAt':
          default:
            aValue = new Date(a.createdAt || 0).getTime();
            bValue = new Date(b.createdAt || 0).getTime();
            break;
        }
        
        if (orderSortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
      
      setPendingOrderApprovals(orders);
      setOrderApprovalTotalPages(response.totalPages || 0);
      setOrderApprovalTotalCount(response.totalItems || response.totalElements || 0);
    } catch (error) {
      console.error('Error loading pending order approvals:', error);
      toast.error('Failed to load pending order approvals');
    } finally {
      setPendingOrderApprovalsLoading(false);
    }
  }, [orderApprovalPage, orderSortField, orderSortDirection]);

  // Handle order approval
  const handleApproveOrder = useCallback(async (orderId) => {
    try {
      setLocalLoading(true);
      await orderService.approveOrder(orderId, user?.id || 'admin');
      await loadPendingOrderApprovals();
      toast.success('Order approved successfully!');
    } catch (error) {
      throw error;
    } finally {
      setLocalLoading(false);
    }
  }, [user, loadPendingOrderApprovals]);

  // Handle order rejection
  const handleRejectOrder = useCallback(async (orderId, rejectionReason) => {
    try {
      setLocalLoading(true);
      await orderService.rejectOrder(orderId, rejectionReason, user?.id || 'admin');
      await loadPendingOrderApprovals();
    } catch (error) {
      throw error;
    } finally {
      setLocalLoading(false);
    }
  }, [user, loadPendingOrderApprovals]);

  // Load pending order approvals when tab is active
  useEffect(() => {
    if (activeTab === 10) {
      loadPendingOrderApprovals();
    }
  }, [activeTab, orderApprovalPage, loadPendingOrderApprovals]);

  // Handler functions for the four buttons
  const handlePendingApprovals = () => {
    setPendingApprovalsDialogOpen(true);
    // Switch to the appropriate tab that shows pending approvals
    setActiveTab(1); // Sellers tab
  };

  const handleActiveDisputes = () => {
    // Mock data for disputes - in real app, this would come from API
    const mockDisputes = [
      {
        id: 1,
        orderId: 'ORD-001',
        customer: 'John Doe',
        seller: 'Tech Store',
        issue: 'Product not as described',
        status: 'Open',
        createdAt: '2024-01-15',
        priority: 'High'
      },
      {
        id: 2,
        orderId: 'ORD-002',
        customer: 'Jane Smith',
        seller: 'Fashion Hub',
        issue: 'Delayed delivery',
        status: 'In Progress',
        createdAt: '2024-01-14',
        priority: 'Medium'
      },
      {
        id: 3,
        orderId: 'ORD-003',
        customer: 'Bob Wilson',
        seller: 'Electronics Plus',
        issue: 'Damaged product received',
        status: 'Open',
        createdAt: '2024-01-13',
        priority: 'High'
      }
    ];
    setDisputes(mockDisputes);
    setActiveDisputesDialogOpen(true);
  };

  const handleGenerateReport = () => {
    setGenerateReportDialogOpen(true);
  };

  const fetchSecurityLogs = async () => {
    try {
      setSecurityLogsLoading(true);
      
      // Calculate date range based on filter
      let startDate = null;
      let endDate = null;
      const now = new Date();
      
      switch (logFilterTimeRange) {
        case 'recent':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000); // Last 24 hours
          endDate = now;
          break;
        case 'last7days':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          endDate = now;
          break;
        case 'last15days':
          startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
          endDate = now;
          break;
        case 'last30days':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          endDate = now;
          break;
        case 'last90days':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          endDate = now;
          break;
        case 'custom':
          if (logFilterCustomStartDate) {
            startDate = new Date(logFilterCustomStartDate);
          }
          if (logFilterCustomEndDate) {
            endDate = new Date(logFilterCustomEndDate);
            endDate.setHours(23, 59, 59, 999); // End of day
          }
          break;
        default:
          // No date filter
          break;
      }
      
      const params = {
        page: 0,
        size: 1000,
        sortBy: 'timestamp',
        sortDir: 'desc'
      };
      
      if (logFilterStatus) {
        params.status = logFilterStatus;
      }
      if (logFilterUserId) {
        params.userId = logFilterUserId;
      }
      if (logFilterUserEmail) {
        params.userEmail = logFilterUserEmail;
      }
      if (startDate) {
        params.startDate = startDate.toISOString();
      }
      if (endDate) {
        params.endDate = endDate.toISOString();
      }
      
      const response = await auditService.getSecurityLogs(params);
      
      // Transform API response to match frontend format
      const logs = (response.logs || []).map((log, index) => ({
        id: log.id || `log-${index + 1}`,
        timestamp: log.timestamp || '',
        event: log.event || log.eventType || 'Unknown Event',
        user: log.user || log.userEmail || log.userId || 'System',
        ipAddress: log.ipAddress || 'unknown',
        status: log.status || 'Unknown',
        details: log.details || '',
        userRole: log.userRole || 'UNKNOWN'
      }));
      
      setSecurityLogs(logs);
    } catch (error) {
      console.error('Failed to fetch security logs:', error);
      toast.error('Failed to load security logs: ' + (error.message || error));
      setSecurityLogs([]);
    } finally {
      setSecurityLogsLoading(false);
    }
  };

  const handleSecurityLogs = async () => {
    setSecurityLogsDialogOpen(true);
    await fetchSecurityLogs();
  };
  
  const handleResetFilters = () => {
    setLogFilterStatus('');
    setLogFilterTimeRange('recent');
    setLogFilterCustomStartDate('');
    setLogFilterCustomEndDate('');
    setLogFilterUserId('');
    setLogFilterUserEmail('');
  };
  
  // Auto-apply filters when they change (with debounce)
  useEffect(() => {
    if (securityLogsDialogOpen) {
      const timeoutId = setTimeout(() => {
        fetchSecurityLogs();
      }, 500); // 500ms debounce
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logFilterStatus, logFilterTimeRange, logFilterCustomStartDate, logFilterCustomEndDate, logFilterUserId, logFilterUserEmail, securityLogsDialogOpen]);
  
  // Export logs functionality
  const handleExportLogs = () => {
    try {
      // Create CSV content
      const headers = ['Timestamp', 'Event', 'User', 'Role', 'IP Address', 'Status', 'Details'];
      const rows = securityLogs.map(log => [
        log.timestamp || '',
        log.event || '',
        log.user || '',
        log.userRole || 'UNKNOWN',
        log.ipAddress || '',
        log.status || '',
        (log.details || '').replace(/"/g, '""') // Escape quotes for CSV
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Logs exported successfully!');
    } catch (error) {
      console.error('Failed to export logs:', error);
      toast.error('Failed to export logs');
    }
  };

  const handleDownloadReport = async () => {
    try {
      // Prepare parameters for API call
      const params = {
        period: reportPeriod,
        year: 2025  // Updated to match the actual order data in the database
      };

      // Add date range for specific periods
      const targetYear = 2025; // Use 2025 to match the actual order data
      const now = new Date();
      switch (reportPeriod) {
        case 'daily':
          params.startDate = now.toISOString().split('T')[0];
          params.endDate = now.toISOString().split('T')[0];
          break;
        case 'weekly':
          const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
          const weekEnd = new Date(now.setDate(weekStart.getDate() + 6));
          params.startDate = weekStart.toISOString().split('T')[0];
          params.endDate = weekEnd.toISOString().split('T')[0];
          break;
        case 'monthly':
          params.startDate = new Date(targetYear, now.getMonth(), 1).toISOString().split('T')[0];
          params.endDate = new Date(targetYear, now.getMonth() + 1, 0).toISOString().split('T')[0];
          break;
        case 'quarterly':
          const quarter = Math.floor(now.getMonth() / 3);
          params.startDate = new Date(targetYear, quarter * 3, 1).toISOString().split('T')[0];
          params.endDate = new Date(targetYear, quarter * 3 + 3, 0).toISOString().split('T')[0];
          break;
        case 'yearly':
          params.startDate = new Date(targetYear, 0, 1).toISOString().split('T')[0];
          params.endDate = new Date(targetYear, 11, 31).toISOString().split('T')[0];
          break;
      }

      // Generate report based on type
      let reportData;
      switch (reportType) {
        case 'sales':
          reportData = await dispatch(generateSalesReport(params)).unwrap();
          break;
        case 'users':
          reportData = await dispatch(generateUserReport(params)).unwrap();
          break;
        case 'products':
          reportData = await dispatch(generateProductReport(params)).unwrap();
          break;
        default:
          throw new Error('Invalid report type');
      }

      // Create Excel workbook with real data
      const workbook = XLSX.utils.book_new();
      let worksheetData = [];
      let reportTitle = '';
      
      const currentDate = new Date().toLocaleDateString();
      
      switch (reportType) {
        case 'sales':
          reportTitle = 'Sales Report';
          
          // Extract data from the correct API response structure
          const salesData = reportData.data || {};
          const summary = salesData.summary || {};
          const monthlyBreakdown = salesData.monthlyBreakdown || {};
          const topCategories = salesData.topCategories || {};
          const orderStatusDistribution = salesData.orderStatusDistribution || {};
          
          // Convert monthly breakdown object to array
          const monthlyArray = Object.values(monthlyBreakdown).map(item => [
            `Month ${item.month}`, item.revenue || 0, item.orders || 0, item.averageOrderValue || 0
          ]);
          
          // Convert top categories object to array with percentages
          const totalCategoryRevenue = Object.values(topCategories).reduce((sum, revenue) => sum + revenue, 0);
          const topCategoriesArray = Object.entries(topCategories).map(([category, revenue]) => {
            const percentage = totalCategoryRevenue > 0 ? ((revenue / totalCategoryRevenue) * 100).toFixed(1) : 0;
            return [category, revenue, '', `${percentage}%`];
          });
          
          // Convert order status distribution object to array with percentages
          const totalOrders = Object.values(orderStatusDistribution).reduce((sum, count) => sum + count, 0);
          const statusDistributionArray = Object.entries(orderStatusDistribution).map(([status, count]) => {
            const percentage = totalOrders > 0 ? ((count / totalOrders) * 100).toFixed(1) : 0;
            return [status, count, `${percentage}%`, ''];
          });
          
          worksheetData = [
            ['Sales Report', '', '', ''],
            ['Generated on:', currentDate, '', ''],
            ['Period:', reportPeriod, '', ''],
            ['', '', '', ''],
            ['Summary Metrics', '', '', ''],
            ['Total Revenue', summary.totalRevenue || 0, 'USD', ''],
            ['Total Orders', summary.totalOrders || 0, '', ''],
            ['Average Order Value', summary.averageOrderValue || 0, 'USD', ''],
            ['', '', '', ''],
            ['Monthly Breakdown', '', '', ''],
            ['Month', 'Revenue', 'Orders', 'AOV'],
            ...monthlyArray,
            ['', '', '', ''],
            ['Top Categories', '', '', ''],
            ['Category', 'Revenue', 'Orders', 'Percentage'],
            ...topCategoriesArray,
            ['', '', '', ''],
            ['Order Status Distribution', '', '', ''],
            ['Status', 'Count', 'Percentage', ''],
            ...statusDistributionArray
          ];
          break;
          
        case 'users':
          reportTitle = 'Users Report';
          
          // Extract data from the correct API response structure
          const usersData = reportData.data || {};
          const roleBreakdown = usersData.roleBreakdown || {};
          const userStatusDistribution = usersData.statusDistribution || {};
          const recentUsers = usersData.recentUsers || [];
          
          // Convert role breakdown object to array with percentages
          const roleArray = Object.entries(roleBreakdown).map(([role, count]) => {
            const percentage = usersData.totalUsers > 0 ? ((count / usersData.totalUsers) * 100).toFixed(1) : 0;
            return [role, count, `${percentage}%`, ''];
          });
          
          // Convert status distribution object to array with percentages
          const statusArray = Object.entries(userStatusDistribution).map(([status, count]) => {
            const percentage = usersData.totalUsers > 0 ? ((count / usersData.totalUsers) * 100).toFixed(1) : 0;
            return [status, count, `${percentage}%`, ''];
          });
          
          worksheetData = [
            ['Users Report', '', '', ''],
            ['Generated on:', currentDate, '', ''],
            ['Period:', reportPeriod, '', ''],
            ['', '', '', ''],
            ['Summary Metrics', '', '', ''],
            ['Total Users', usersData.totalUsers || 0, '', ''],
            ['Active Users', usersData.activeUsers || 0, '', ''],
            ['Inactive Users', usersData.inactiveUsers || 0, '', ''],
            ['Pending Users', usersData.pendingUsers || 0, '', ''],
            ['', '', '', ''],
            ['Role Breakdown', '', '', ''],
            ['Role', 'Count', 'Percentage', ''],
            ...roleArray,
            ['', '', '', ''],
            ['Status Distribution', '', '', ''],
            ['Status', 'Count', 'Percentage', ''],
            ...statusArray,
            ['', '', '', ''],
            ['Recent Users', '', '', ''],
            ['Name', 'Email', 'Role', 'Created Date'],
            ...recentUsers.map(user => [
              `${user.firstName} ${user.lastName}`, user.email, user.role, user.createdAt
            ])
          ];
          break;
          
        case 'products':
          reportTitle = 'Products Report';
          
          // Extract data from the correct API response structure
          const productsData = reportData.data || {};
          const categoryBreakdown = productsData.categoryBreakdown || {};
          const productStatusDistribution = productsData.statusDistribution || {};
          const recentProducts = productsData.recentProducts || [];
          
          // Convert category breakdown object to array with percentages
          const categoryArray = Object.entries(categoryBreakdown).map(([category, count]) => {
            const percentage = productsData.totalProducts > 0 ? ((count / productsData.totalProducts) * 100).toFixed(1) : 0;
            return [category, count, `${percentage}%`, ''];
          });
          
          // Convert status distribution object to array with percentages
          const productStatusArray = Object.entries(productStatusDistribution).map(([status, count]) => {
            const percentage = productsData.totalProducts > 0 ? ((count / productsData.totalProducts) * 100).toFixed(1) : 0;
            return [status, count, `${percentage}%`, ''];
          });
          
          worksheetData = [
            ['Products Report', '', '', ''],
            ['Generated on:', currentDate, '', ''],
            ['Period:', reportPeriod, '', ''],
            ['', '', '', ''],
            ['Summary Metrics', '', '', ''],
            ['Total Products', productsData.totalProducts || 0, '', ''],
            ['Active Products', productsData.activeProducts || 0, '', ''],
            ['Inactive Products', productsData.inactiveProducts || 0, '', ''],
            ['Pending Products', productsData.pendingProducts || 0, '', ''],
            ['', '', '', ''],
            ['Category Breakdown', '', '', ''],
            ['Category', 'Count', 'Percentage', ''],
            ...categoryArray,
            ['', '', '', ''],
            ['Status Distribution', '', '', ''],
            ['Status', 'Count', 'Percentage', ''],
            ...productStatusArray,
            ['', '', '', ''],
            ['Recent Products', '', '', ''],
            ['Name', 'Category', 'Status', 'Created Date'],
            ...recentProducts.map(product => [
              product.name, product.category, product.status, product.createdAt
            ])
          ];
          break;
      }
      
      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Set column widths for better formatting
      const columnWidths = [
        { wch: 25 }, // Column A
        { wch: 20 }, // Column B
        { wch: 15 }, // Column C
        { wch: 20 }  // Column D
      ];
      worksheet['!cols'] = columnWidths;
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, reportTitle);
      
      // Generate filename
      const fileName = `${reportType}_report_${reportPeriod}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Write and download the file
      XLSX.writeFile(workbook, fileName);
      
      toast.success('Excel report downloaded successfully!');
      setGenerateReportDialogOpen(false);
    } catch (error) {
      console.error('Error generating Excel report:', error);
      toast.error('Failed to generate Excel report: ' + (error.message || 'Unknown error'));
    }
  };

  const handleApprove = async (type, id) => {
    try {
      setLocalLoading(true);
      if (type === 'seller') {
        await dispatch(approveSeller(id)).unwrap();
        toast.success('Seller approved successfully!');
        dispatch(fetchPendingSellers());
      } else if (type === 'product') {
        await dispatch(approveProduct(id)).unwrap();
        toast.success('Product approved successfully!');
        dispatch(fetchPendingProducts({ fetchAll: true }));
      }
    } catch (error) {
      toast.error(error || `Failed to approve ${type}`);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleReject = (type, id) => {
    if (type === 'seller') {
      const seller = getPendingSellersArray().find((item) => item.id === id);
      if (seller) {
        handleRejectSeller(seller);
      }
    } else if (type === 'product') {
      const product = getPendingProductsArray().find((item) => item.id === id);
      if (product) {
        openProductRejectionDialog(product);
      }
    }
  };
  
  const OverviewTab = () => (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={dashboardStats.totalUsers.value}
            icon={<People />}
            trend={dashboardStats.totalUsers.trend}
            period={dashboardStats.totalUsers.period}
            color="primary"
            loading={analyticsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Sellers"
            value={dashboardStats.totalSellers.value}
            icon={<Store />}
            trend={dashboardStats.totalSellers.trend}
            period={dashboardStats.totalSellers.period}
            color="secondary"
            loading={analyticsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Products"
            value={dashboardStats.totalProducts.value}
            icon={<Inventory />}
            trend={dashboardStats.totalProducts.trend}
            period={dashboardStats.totalProducts.period}
            color="success"
            loading={analyticsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Monthly Revenue"
            value={dashboardStats.totalRevenue.value}
            icon={<AttachMoney />}
            trend={dashboardStats.totalRevenue.trend}
            period={dashboardStats.totalRevenue.period}
            color="warning"
            loading={analyticsLoading}
          />
        </Grid>
      </Grid>
      
      {/* Charts */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 3,
          mb: 4
        }}
      >
        <Card
          sx={{
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid rgba(22,101,52,0.14)',
            background: 'linear-gradient(150deg, #ECFDF5 0%, #FFFFFF 52%, #F0FDF4 100%)',
            boxShadow: '0 18px 38px rgba(22, 101, 52, 0.1)',
          }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#064E3B' }}>
                  Top Customers by Total Purchase
                </Typography>
                <Typography variant="body2" sx={{ color: '#047857' }}>
                  {topCustomersData.length
                    ? `Combined purchase value ₹${(
                        topCustomersData.reduce((sum, customer) => sum + customer.totalPurchase, 0) / 100000
                      ).toFixed(1)}L`
                    : 'Track your highest-value customers and their order volumes'}
                </Typography>
              </Box>
              {topCustomerHighlightMetrics.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1.25, sm: 2 },
                    flexWrap: 'wrap',
                    background: 'rgba(4, 120, 87, 0.12)',
                    border: '1px solid rgba(4, 120, 87, 0.22)',
                    borderRadius: 2,
                    px: { xs: 1.5, md: 2.5 },
                    py: 1.25,
                  }}
                >
                  {topCustomerHighlightMetrics.map((metric, metricIndex) => (
                    <React.Fragment key={metric.label}>
                      {metricIndex > 0 && (
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            borderColor: 'rgba(4, 120, 87, 0.25)',
                            mx: { xs: 0, sm: 0.5 },
                            display: { xs: 'none', sm: 'block' },
                          }}
                        />
                      )}
                      <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#0F172A', opacity: 0.75 }}>
                          {metric.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            color: '#0B3B2E',
                            lineHeight: 1.4,
                          }}
                        >
                          {metric.value}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minHeight: 260, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {customerAnalyticsLoading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : customerAnalyticsError ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#64748b',
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                      Failed to load top customer insights
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {customerAnalyticsError || 'Please try again later.'}
                    </Typography>
                    <Button variant="contained" size="small" onClick={fetchCustomerAnalytics}>
                      Retry
                    </Button>
                  </Box>
                </Box>
              ) : topCustomersData.length === 0 ? (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#64748b',
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      No customer purchase data yet
                    </Typography>
                    <Typography variant="body2">
                      Complete customer orders to populate this insight.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topCustomersData}
                      margin={{ top: 16, right: 24, bottom: 5, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="4 6" stroke="#D1FAE5" vertical={false} />
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#065F46', fontSize: 12 }}
                      angle={-30}
                      textAnchor="end"
                        height={75}
                        interval={0}
                    />
                    <YAxis
                      yAxisId="purchase"
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={formatCompactCurrency}
                      tick={{ fill: '#047857', fontSize: 12 }}
                      width={70}
                    />
                    <YAxis
                      yAxisId="orders"
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                      tick={{ fill: '#34D399', fontSize: 12 }}
                      width={40}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        borderRadius: 12,
                        border: '1px solid rgba(16,185,129,0.18)',
                        color: '#064E3B',
                        boxShadow: '0 16px 32px rgba(20,83,45,0.15)',
                      }}
                      formatter={(value, name) => {
                        if (name === 'Total Purchase') {
                          return [formatCurrency(value), 'Total Purchase'];
                        }
                        if (name === 'Orders') {
                          return [value, 'Orders'];
                        }
                        if (name === 'Average Order Value') {
                          return [formatCurrency(value), 'Average Order Value'];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ paddingBottom: 8 }} />
                    <Bar
                      yAxisId="purchase"
                      dataKey="totalPurchase"
                      name="Total Purchase"
                      fill="#34D399"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={36}
                    />
                    <Line
                      type="monotone"
                      dataKey={(entry) =>
                        entry.orders > 0 ? entry.totalPurchase / entry.orders : 0
                      }
                      name="Average Order Value"
                      yAxisId="purchase"
                      stroke="#047857"
                      strokeWidth={2.5}
                      dot={{ r: 4, stroke: '#047857', strokeWidth: 2, fill: '#fff' }}
                      activeDot={{ r: 6 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="orders"
                      name="Orders"
                      yAxisId="orders"
                      stroke="#2563EB"
                      strokeWidth={2.2}
                      dot={{ r: 3, stroke: '#2563EB', strokeWidth: 2, fill: '#DBEAFE' }}
                      activeDot={{ r: 5 }}
                    />
                  </BarChart>
                </ResponsiveContainer>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
        
        <Card
          sx={{
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 3,
            background: 'linear-gradient(150deg, #DBEAFE 0%, #FFFFFF 50%, #BAE6FD 100%)',
            border: '1px solid rgba(14,165,233,0.14)',
            boxShadow: '0 18px 38px rgba(14, 165, 233, 0.1)',
          }}
        >
          <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  Category Performance
                </Typography>
                <Typography variant="body2" sx={{ color: '#047857' }}>
                  {categoryData.length
                    ? `Tracking ${categoryData.length} top-performing categories`
                    : 'Monitor product distribution across categories'}
                </Typography>
              </Box>
              {categoryData.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#64748b' }}>
                      Leading Category
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                      {categoryData[0]?.name || '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#64748b' }}>
                      Share
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#0F172A' }}>
                      {categoryData[0]?.percentage?.toFixed(1) ?? 0}%
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ flex: 1, minHeight: 260 }}>
              {(analyticsLoading || categoriesLoading) ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : categoryData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      dataKey="count"
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={115}
                      paddingAngle={4}
                      cornerRadius={10}
                      labelLine={false}
                      label={renderCategoryLabel}
                    >
                      {categoryData.map((entry, index) => {
                        // Ensure vibrant colors - use palette colors directly if available
                        const vibrantColor = entry.color || CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length];
                        return (
                        <Cell
                          key={`category-cell-${index}`}
                            fill={vibrantColor}
                          stroke="#FFFFFF"
                            strokeWidth={3}
                            style={{
                              filter: 'brightness(1.1) saturate(1.3)',
                              opacity: 1,
                            }}
                        />
                        );
                      })}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name, props) => [
                        `${value.toLocaleString('en-IN')} products`,
                        `${props.payload.name} • ${props.payload.percentage.toFixed(1)}%`,
                      ]}
                      contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: 12,
                        border: 'none',
                        color: '#1E293B',
                        boxShadow: '0 16px 32px rgba(15, 23, 42, 0.25)',
                      }}
                      itemStyle={{ color: '#0F172A' }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={54}
                      iconType="circle"
                      wrapperStyle={{
                        fontSize: '13px',
                        fontWeight: 600,
                        paddingTop: 12,
                        color: '#0F172A',
                      }}
                      formatter={(value, entry) => (
                        <span style={{ color: '#0F172A', fontWeight: 600 }}>
                          {value}{' '}
                          <span style={{ color: entry.payload.color, fontWeight: 600 }}>
                            ({entry.payload.percentage.toFixed(1)}%)
                          </span>
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#64748b',
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Category analytics coming soon
                    </Typography>
                    <Typography variant="body2">
                      Add products by category to see how your catalogue is distributed.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
      
      {/* Recent Activities */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Recent Activities
            </Typography>
            <Button 
              startIcon={<Refresh />} 
              onClick={fetchRecentActivities}
              disabled={recentActivitiesLoading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          
          {recentActivitiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : recentActivities.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No recent activities to display
              </Typography>
            </Box>
          ) : (
            <List>
              {recentActivities.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: 'grey.50',
                      '&:hover': {
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${activity.color}.main`,
                          width: 40,
                          height: 40,
                        }}
                      >
                        {activity.icon}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {activity.title}
                          </Typography>
                          {activity.status && (
                            <Chip
                              label={activity.status}
                              size="small"
                              color={
                                activity.status === 'PENDING' ? 'warning' :
                                activity.status === 'COMPLETED' || activity.status === 'DELIVERED' ? 'success' :
                                activity.status === 'CANCELLED' ? 'error' : 'default'
                              }
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                            {activity.description}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {activity.timestamp
                              ? new Date(activity.timestamp).toLocaleString('en-IN', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'Unknown time'}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  {index < recentActivities.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
  
  const renderSellerTable = (sellers, actions) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSellerSort('businessName')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Business Name
                {sellerSortField === 'businessName' && (
                  sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                )}
              </Box>
            </TableCell>
            <TableCell
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSellerSort('businessType')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Type
                {sellerSortField === 'businessType' && (
                  sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                )}
              </Box>
            </TableCell>
            <TableCell
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSellerSort('gstNumber')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                GST Number
                {sellerSortField === 'gstNumber' && (
                  sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                )}
              </Box>
            </TableCell>
            <TableCell
              sx={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => handleSellerSort('createdAt')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Submitted
                {sellerSortField === 'createdAt' && (
                  sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                )}
              </Box>
            </TableCell>
            <TableCell>Documents</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {getSortedSellers(sellers).map((seller) => (
            <TableRow key={seller.id}>
              <TableCell>
                <Box>
                  <Typography variant="subtitle2">
                    {seller.businessProfile?.businessName || `${seller.firstName} ${seller.lastName}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {seller.email}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={seller.businessProfile?.businessType || 'N/A'}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{seller.businessProfile?.gstNumber || 'N/A'}</TableCell>
              <TableCell>
                {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString() : 'N/A'}
              </TableCell>
              <TableCell>
                <Chip
                  label={seller.businessProfile ? '1 profile' : '0 docs'}
                  size="small"
                  color={seller.businessProfile ? 'primary' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => {
                      setSelectedSeller(seller);
                      setSellerDialogOpen(true);
                    }}
                  >
                    View
                  </Button>
                  {actions === 'pending' && (
                    <>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApproveSeller(seller)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleRejectSeller(seller)}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {actions === 'rejected' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => handleApproveSeller(seller)}
                    >
                      Approve
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const SellersTab = () => {
    const pendingCount = getPendingSellersArray().length;
    const approvedCount = getApprovedSellersArray().length;
    const rejectedCount = getRejectedSellersArray().length;

    return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Seller Management</Typography>
              <Button startIcon={<Refresh />} onClick={handleRefreshData} disabled={adminLoading || localLoading}>
                Refresh
              </Button>
            </Box>

            {/* 3 sub-tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={sellerStatusTab} onChange={(e, v) => setSellerStatusTab(v)}>
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Pending Approval
                      <Box component="span" sx={{
                        bgcolor: '#f59e0b', color: '#fff', borderRadius: '50%',
                        minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, px: 0.5,
                      }}>
                        {pendingCount}
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Approved
                      <Box component="span" sx={{
                        bgcolor: '#10b981', color: '#fff', borderRadius: '50%',
                        minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, px: 0.5,
                      }}>
                        {approvedCount}
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Rejected
                      <Box component="span" sx={{
                        bgcolor: '#ef4444', color: '#fff', borderRadius: '50%',
                        minWidth: 22, height: 22, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, px: 0.5,
                      }}>
                        {rejectedCount}
                      </Box>
                    </Box>
                  }
                />
              </Tabs>
            </Box>

            {/* Pending tab */}
            {sellerStatusTab === 0 && (
              <>
                {pendingSellersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : pendingCount === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <Typography>No pending seller applications.</Typography>
                  </Box>
                ) : (
                  renderSellerTable(getPendingSellersArray(), 'pending')
                )}
              </>
            )}

            {/* Approved tab */}
            {sellerStatusTab === 1 && (
              <>
                {approvedSellersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : approvedCount === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <Typography>No approved sellers found.</Typography>
                  </Box>
                ) : (
                  renderSellerTable(getApprovedSellersArray(), 'approved')
                )}
              </>
            )}

            {/* Rejected tab */}
            {sellerStatusTab === 2 && (
              <>
                {rejectedSellersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : rejectedCount === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <Typography>No rejected sellers found.</Typography>
                  </Box>
                ) : (
                  renderSellerTable(getRejectedSellersArray(), 'rejected')
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };
  
  // Sorting functionality for products
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField, sortDirection]);

  const getSortedProducts = useCallback((products) => {
    if (!sortField || !products) return products;
    
    const sorted = [...products].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle special cases
      if (sortField === 'sellerName') {
        aValue = a.sellerName || '';
        bValue = b.sellerName || '';
      } else if (sortField === 'createdAt') {
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
      } else if (sortField === 'price') {
        aValue = parseFloat(a.price || 0);
        bValue = parseFloat(b.price || 0);
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [sortField, sortDirection]);

  const sortedPendingProducts = useMemo(
    () => getSortedProducts(pendingProductsArray) || [],
    [pendingProductsArray, getSortedProducts]
  );

  // Sorting functionality for sellers
  const handleSellerSort = (field) => {
    if (sellerSortField === field) {
      setSellerSortDirection(sellerSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSellerSortField(field);
      setSellerSortDirection('asc');
    }
  };

  const getSortedSellers = (sellers) => {
    if (!sellerSortField || !sellers) return sellers;
    
    const sorted = [...sellers].sort((a, b) => {
      let aValue, bValue;
      
      switch (sellerSortField) {
        case 'businessName':
          aValue = a.businessProfile?.businessName || `${a.firstName} ${a.lastName}` || '';
          bValue = b.businessProfile?.businessName || `${b.firstName} ${b.lastName}` || '';
          break;
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'businessType':
          aValue = a.businessProfile?.businessType || '';
          bValue = b.businessProfile?.businessType || '';
          break;
        case 'gstNumber':
          aValue = a.businessProfile?.gstNumber || '';
          bValue = b.businessProfile?.gstNumber || '';
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || a.registrationDate || 0);
          bValue = new Date(b.createdAt || b.registrationDate || 0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sellerSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sellerSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const FinancialTab = () => (
    <Box>
      {/* Data Source Info */}
      {monthlyRevenueFromCompletedOrders && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Financial data shown below is based on completed/delivered orders only for year {monthlyRevenueFromCompletedOrders.year}.
          </Typography>
        </Alert>
      )}
      
      {/* Financial Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Total Revenue</Typography>
              {monthlyRevenueFromCompletedOrdersLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography variant="h4" color="primary">₹{(dashboardStats.totalRevenue.value / 100000).toFixed(1)}L</Typography>
                  <Typography variant="body2" color="text.secondary">From completed orders</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Commission Earned</Typography>
              {monthlyRevenueFromCompletedOrdersLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography variant="h4" color="success.main">₹{(dashboardStats.totalRevenue.value * 0.05 / 100000).toFixed(1)}L</Typography>
                  <Typography variant="body2" color="text.secondary">5% platform fee</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Pending Payouts</Typography>
              {monthlyRevenueFromCompletedOrdersLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography variant="h4" color="warning.main">₹{(dashboardStats.totalRevenue.value * 0.15 / 100000).toFixed(1)}L</Typography>
                  <Typography variant="body2" color="text.secondary">To be processed</Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Revenue Chart */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Monthly Revenue & Orders from Completed Orders
            </Typography>
            {monthlyRevenueFromCompletedOrdersLoading && <CircularProgress size={20} />}
          </Box>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" orientation="left" />
              <YAxis yAxisId="right" orientation="right" />
              <RechartsTooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenue (₹)" />
              <Bar yAxisId="right" dataKey="orders" fill="#82ca9d" name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
  
  // Analytics Tab Component
  const AnalyticsTab = () => {
    const [selectedAnalytics, setSelectedAnalytics] = useState(null);
    const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);

    const handleViewAnalytics = (type) => {
      setSelectedAnalytics(type);
      setAnalyticsDialogOpen(true);
    };

    const renderSalesChart = () => {
      // Use real data from analytics state with fallbacks
      const orderAnalytics = analytics?.orders || {};
      // Use paymentAnalytics from Redux state (already available from useSelector above)
      const paymentAnalyticsData = paymentAnalytics || analytics?.payments || {};
      
      // Sales by Category Data (Donut Chart) - Enhanced real data integration
      const salesByCategoryData = analytics?.categoryBreakdown && analytics.categoryBreakdown.length > 0 ? 
        analytics.categoryBreakdown.map((category, index) => ({
          category: category.name || category.category || `Category ${index + 1}`,
          revenue: category.sales || category.revenue || category.value * 75000, // Better revenue estimation
          percentage: category.percentage || category.value || ((category.sales || category.revenue || 0) / (analytics?.totalRevenue || 1) * 100).toFixed(1),
          color: ['#2C3E50', '#3498DB', '#E74C3C', '#F39C12', '#27AE60', '#9B59B6'][index % 6],
          orders: category.orders || Math.floor((category.value || 0) / 100 * (analytics?.totalOrders || 500))
        })) : productCategories.length > 0 ? 
        productCategories.slice(0, 6).map((category, index) => {
          const totalProducts = productCategories.reduce((sum, cat) => sum + (cat.count || 0), 0);
          const percentage = totalProducts > 0 ? ((category.count || 0) / totalProducts * 100).toFixed(1) : 0;
          return {
            category: category.name,
            revenue: (category.count || 0) * 180000, // Better revenue estimation
            percentage: parseFloat(percentage),
            color: ['#2C3E50', '#3498DB', '#E74C3C', '#F39C12', '#27AE60', '#9B59B6'][index % 6],
            orders: Math.floor((category.count || 0) * 3.2) // Better order estimation
          };
        }) : [
        { category: 'Marble', revenue: 3200000, percentage: 35.2, color: '#2C3E50', orders: 234 },
        { category: 'Granite', revenue: 2800000, percentage: 30.8, color: '#3498DB', orders: 189 },
        { category: 'Tiles', revenue: 1800000, percentage: 19.8, color: '#E74C3C', orders: 145 },
        { category: 'Limestone', revenue: 1300000, percentage: 14.2, color: '#F39C12', orders: 98 }
      ];

      // COD vs Other Payment Methods Data (Bar Chart) - Real data from DB
      const codVsOtherPaymentsData = (() => {
        console.log('═══════════════════════════════════════════════════');
        console.log('🔍 COD vs Other Payments - Data Source Analysis');
        console.log('═══════════════════════════════════════════════════');
        console.log('📦 Payment Analytics Data (from analytics slice):', paymentAnalyticsData);
        console.log('📦 Analytics State (from admin slice):', analytics);
        console.log('📊 Analytics methodDistribution:', analytics?.methodDistribution);
        console.log('📊 Analytics payments:', analytics?.payments);
        console.log('📊 Analytics payments methodDistribution:', analytics?.payments?.methodDistribution);
        
        // Try multiple sources for method distribution
        let methodDist = null;
        let dataSource = 'none';
        
        // First priority: Use paymentAnalyticsData.methodDistribution if available
        if (paymentAnalyticsData?.methodDistribution && Object.keys(paymentAnalyticsData.methodDistribution).length > 0) {
          console.log('✅ Using paymentAnalyticsData.methodDistribution:', paymentAnalyticsData.methodDistribution);
          methodDist = paymentAnalyticsData.methodDistribution;
          dataSource = 'paymentAnalytics';
        }
        // Second priority: Use analytics.methodDistribution directly
        else if (analytics?.methodDistribution && Object.keys(analytics.methodDistribution).length > 0) {
          console.log('✅ Using analytics.methodDistribution:', analytics.methodDistribution);
          methodDist = analytics.methodDistribution;
          dataSource = 'analytics.methodDistribution';
        }
        // Third priority: Use analytics.payments.methodDistribution
        else if (analytics?.payments?.methodDistribution && Object.keys(analytics.payments.methodDistribution).length > 0) {
          console.log('✅ Using analytics.payments.methodDistribution:', analytics.payments.methodDistribution);
          methodDist = analytics.payments.methodDistribution;
          dataSource = 'analytics.payments.methodDistribution';
        }
        
        console.log('📍 Data Source Selected:', dataSource);
        console.log('📍 Method Distribution Object:', methodDist);
        
        if (methodDist) {
          // Calculate COD and Other payment counts
          let codCount = 0;
          let otherCount = 0;
          
          console.log('🔄 Processing method distribution entries...');
          Object.entries(methodDist).forEach(([method, count]) => {
            console.log(`   - ${method}: ${count}`);
            // Normalize method name and check for COD variations
            const normalizedMethod = method.toUpperCase().trim();
            if (normalizedMethod === 'CASH_ON_DELIVERY' || 
                normalizedMethod === 'COD' || 
                normalizedMethod.includes('COD') ||
                normalizedMethod.includes('CASH')) {
              codCount += count;
              console.log(`     ✓ Added to COD count (now ${codCount})`);
            } else {
              otherCount += count;
              console.log(`     ✓ Added to Other count (now ${otherCount})`);
            }
          });
          
          const totalCount = codCount + otherCount;
          
          console.log('═══════════════════════════════════════════════════');
          console.log('📊 FINAL COUNTS:');
          console.log('   COD Count:', codCount);
          console.log('   Other Payment Methods Count:', otherCount);
          console.log('   Total Payments:', totalCount);
          console.log('═══════════════════════════════════════════════════');
          
          if (totalCount === 0) {
            console.log('⚠️ Total count is 0 - No payment data available');
            return [];
          }
          
          const codPercentage = totalCount > 0 ? ((codCount / totalCount) * 100).toFixed(1) : 0;
          const otherPercentage = totalCount > 0 ? ((otherCount / totalCount) * 100).toFixed(1) : 0;
          
          const result = [
            {
              name: 'Cash on Delivery',
              count: codCount,
              percentage: parseFloat(codPercentage),
              color: '#F39C12'
            },
            {
              name: 'Other Payment Methods',
              count: otherCount,
              percentage: parseFloat(otherPercentage),
              color: '#3498DB'
            }
          ];
          
          console.log('✅ COD vs Other Payments Data Result:', result);
          console.log('═══════════════════════════════════════════════════');
          return result;
        }
        
        // No data found - return empty array
        console.log('❌ No payment method data found in any source');
        console.log('═══════════════════════════════════════════════════');
        return [];
      })();

      // Top 10 Products by Quantity Sold Data (Bar Chart) - ONLY USE REAL DATA
      const topProductsByQuantityData = topProducts && topProducts.length > 0 ? 
        topProducts.slice(0, 10).map(product => ({
          name: product.name || product.productName || product.title || 'Product',
          quantity: product.quantitySold || product.quantity || product.sold || 0,
          revenue: product.revenue || product.totalRevenue || product.sales || 0,
          category: product.category || 'General'
        })) : reports?.sales?.topProducts && reports.sales.topProducts.length > 0 ? 
        reports.sales.topProducts.slice(0, 10).map(product => ({
          name: product.name || product.productName || product.title || 'Product',
          quantity: product.quantitySold || product.quantity || product.sold || 0,
          revenue: product.revenue || product.totalRevenue || product.sales || 0,
          category: product.category || 'General'
        })) : analytics?.topProducts && analytics.topProducts.length > 0 ?
        analytics.topProducts.slice(0, 10).map(product => ({
          name: product.name || product.productName || product.title || 'Product',
          quantity: product.quantity || product.sold || product.orders || 0,
          revenue: product.revenue || product.sales || product.amount || 0,
          category: product.category || 'General'
        })) : [];
      
      // Calculate total items sold from real data
      const totalItemsSold = topProductsByQuantityData.reduce((sum, product) => sum + product.quantity, 0);

      return (
        <Box sx={{ width: '100%', p: 2 }}>
          <Typography variant="h5" sx={{ 
            mb: 3, 
            textAlign: 'center', 
            fontWeight: 600, 
            color: 'white',
            backgroundColor: '#2C3E50',
            p: 2,
            borderRadius: 1
          }}>
            Sales Analytics Dashboard
          </Typography>
          
          {/* CSS Grid Layout */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              mb: 3
            }}
          >
            {/* Sales by Category (Donut Chart) */}
            <Card sx={{ height: '400px', border: '2px solid #2C3E50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Sales by Category</Typography>
                  {(analyticsLoading || categoriesLoading) && <CircularProgress size={20} />}
                </Box>
                {(analyticsLoading || categoriesLoading) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={salesByCategoryData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="revenue"
                      label={({ category, percentage }) => `${category}: ${percentage}%`}
                      labelStyle={{ fill: "#0F172A", fontWeight: 600 }}
                      labelLine={false}
                    >
                      {salesByCategoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} opacity={1}/>
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [`₹${(value/100000).toFixed(1)}L`, 'Revenue']}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend 
                      formatter={(value, entry) => entry.payload.category}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '12px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* COD vs Other Payment Methods (Bar Chart) */}
            <Card sx={{ height: '400px', border: '2px solid #2C3E50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    COD vs Other Payment Methods
                  </Typography>
                  {(paymentAnalytics?.methodDistribution || analytics?.methodDistribution) && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                {analyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <CircularProgress />
                  </Box>
                ) : codVsOtherPaymentsData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No Payment Data Available</Typography>
                    <Typography variant="body2" color="text.secondary">Complete some payments to see distribution</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart 
                    data={codVsOtherPaymentsData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#666"
                      fontSize={14}
                      fontWeight={500}
                    />
                    <YAxis 
                      stroke="#666"
                      label={{ value: 'Number of Payments', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                    />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        const entry = codVsOtherPaymentsData.find(d => d.count === value);
                        return [
                          `${value} payments (${entry?.percentage || 0}%)`,
                          'Count'
                        ];
                      }}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                      labelStyle={{ fontWeight: 600, marginBottom: 8 }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                      formatter={(value, entry) => `${entry.payload.name}: ${entry.payload.count} (${entry.payload.percentage}%)`}
                    />
                    <Bar 
                      dataKey="count" 
                      name="Payment Count"
                      radius={[8, 8, 0, 0]}
                      label={{ 
                        position: 'top', 
                        formatter: (value) => `${value} payments`,
                        style: { fontSize: 12, fontWeight: 600 }
                      }}
                    >
                      {codVsOtherPaymentsData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Third Chart - Centered */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              mb: 3
            }}
          >
            <Card 
              sx={{ 
                gridColumn: '1 / 3',
                justifySelf: 'center',
                maxWidth: '50%',
                width: '100%',
                border: '2px solid #2C3E50'
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Top 10 Products by Quantity Sold
                  {topProducts && topProducts.length > 0 && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {topProductsLoading || analyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <CircularProgress />
                  </Box>
                ) : topProductsByQuantityData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">
                      No Product Data Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Complete some orders to see top selling products
                    </Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={topProductsByQuantityData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#666" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      interval={0}
                      fontSize={12}
                    />
                    <YAxis yAxisId="left" stroke="#8884d8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'Revenue') return [`₹${(value/100000).toFixed(1)}L`, 'Revenue'];
                        return [value, 'Quantity Sold'];
                      }}
                      labelFormatter={(label) => `Product: ${label}`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="quantity" fill="#8884d8" name="Quantity Sold" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="revenue" fill="#82ca9d" name="Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Key Sales Metrics */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              mb: 3
            }}
          >
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {analytics?.totalRevenue ? `₹${(analytics.totalRevenue/100000).toFixed(1)}L` : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Revenue</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {analytics?.totalRevenue ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    From all orders
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {analytics?.totalOrders ? analytics.totalOrders.toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Orders</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {analytics?.totalOrders ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    All time orders
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalItemsSold > 0 ? totalItemsSold.toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Items Sold</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {totalItemsSold > 0 ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    From top 10 products
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
          </Box>
        </Box>
      );
    };

    const renderCustomerChart = () => {
      // Use real data from analytics state with fallbacks
      const userAnalytics = analytics?.users || {};
      
      // Top Customers by Total Purchase Data (Bar Chart) - ONLY USE REAL DATA
      // User Distribution Data (Donut Chart) - ONLY USE REAL DATA
      const userDistributionData = userTypeDistribution ? 
        Object.entries(userTypeDistribution).map(([userType, count], index) => {
          const totalUsers = Object.values(userTypeDistribution).reduce((sum, val) => sum + val, 0);
          const percentage = totalUsers > 0 ? ((count / totalUsers) * 100).toFixed(1) : 0;
          const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1'];
          return {
            userType: userType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            count: count,
            percentage: parseFloat(percentage),
            color: colors[index % colors.length]
          };
        }) : [];

      // Total Orders by Customer Data (Bar Chart) - ONLY USE REAL DATA
      // Sort by totalOrders descending and show all customers (not limited)
      const ordersByCustomerData = topCustomers && topCustomers.length > 0 ? 
        [...topCustomers]
          .sort((a, b) => {
            const aOrders = Number(a.totalOrders ?? a.orders ?? 0);
            const bOrders = Number(b.totalOrders ?? b.orders ?? 0);
            return bOrders - aOrders;
          })
          .map(customer => ({
            customerName: customer.name || customer.customerName || `Customer ${customer.id}`,
            totalOrders: customer.totalOrders || customer.orders || 0,
            completedOrders: Math.floor((customer.totalOrders || customer.orders || 0) * 0.9), // Estimated as 90% completed
            pendingOrders: Math.ceil((customer.totalOrders || customer.orders || 0) * 0.1), // Estimated as 10% pending
            totalValue: customer.totalPurchase || customer.totalSpent || customer.revenue || 0
          })) : [];

      return (
        <Box sx={{ width: '100%', p: 2 }}>
          <Typography variant="h5" sx={{ 
            mb: 3, 
            textAlign: 'center', 
            fontWeight: 600, 
            color: 'white',
            backgroundColor: '#2C3E50',
            p: 2,
            borderRadius: 1
          }}>
            Customer Analytics Dashboard
          </Typography>
          
          {/* First Row: Two charts side by side */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              mb: 3
            }}
          >
            {/* User Distribution (Donut Chart) */}
            <Card sx={{ height: '400px', border: '2px solid #2C3E50' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  User Distribution
                  {userTypeDistribution && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {customerAnalyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <CircularProgress />
                  </Box>
                ) : customerAnalyticsError ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                      Error loading user distribution data
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={fetchCustomerAnalytics}
                      sx={{ textTransform: 'none' }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : userDistributionData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No User Data Available</Typography>
                    <Typography variant="body2" color="text.secondary">Complete user registrations to see distribution</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={userDistributionData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ userType, percentage }) => `${userType}: ${percentage}%`}
                      labelLine={false}
                    >
                      {userDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [value.toLocaleString(), 'Customers']}
                      labelFormatter={(label) => `User Type: ${label}`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Total Orders by Customer (Bar Chart) */}
            <Card sx={{ height: '400px', border: '2px solid #2C3E50' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Total Orders by Customer
                  {topCustomers && topCustomers.length > 0 && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {customerAnalyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <CircularProgress />
                  </Box>
                ) : customerAnalyticsError ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                      Error loading customer order data
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={fetchCustomerAnalytics}
                      sx={{ textTransform: 'none' }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : ordersByCustomerData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No Customer Orders</Typography>
                    <Typography variant="body2" color="text.secondary">Orders from customers will appear here</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={ordersByCustomerData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="customerName" 
                      stroke="#666" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      interval={0}
                      fontSize={10}
                    />
                    <YAxis stroke="#666" />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'Total Value') return [`₹${(value/100000).toFixed(1)}L`, 'Order Value'];
                        return [value, name];
                      }}
                      labelFormatter={(label) => `Customer: ${label}`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="completedOrders" stackId="orders" fill="#4caf50" name="Completed Orders" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="pendingOrders" stackId="orders" fill="#ff9800" name="Pending Orders" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Second Row: One centered chart */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              mb: 3
            }}
          >
            {/* Top Customers by Total Purchase (Bar Chart) */}
            <Card 
              sx={{
                gridColumn: '1 / 3',
                justifySelf: 'center',
                maxWidth: '50%',
                width: '100%',
                border: '2px solid #2C3E50'
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Top Customers by Total Purchase
                  {topCustomers && topCustomers.length > 0 && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {customerAnalyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <CircularProgress />
                  </Box>
                ) : customerAnalyticsError ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                      Error loading top customers data
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={fetchCustomerAnalytics}
                      sx={{ textTransform: 'none' }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : topCustomersData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No Customer Purchase Data</Typography>
                    <Typography variant="body2" color="text.secondary">Complete customer orders to see purchase analytics</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={topCustomersData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#666" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      interval={0}
                      fontSize={12}
                    />
                    <YAxis yAxisId="left" stroke="#8884d8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'Total Purchase') return [`₹${(value/100000).toFixed(1)}L`, 'Total Purchase'];
                        if (name === 'Avg Order') return [`₹${(value/1000).toFixed(0)}K`, 'Avg Order Value'];
                        return [value, name];
                      }}
                      labelFormatter={(label) => `Customer: ${label}`}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="totalPurchase" fill="#8884d8" name="Total Purchase" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="orders" fill="#82ca9d" name="Orders Count" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Customer Metrics Cards */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              mb: 3
            }}
          >
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {analytics?.totalUsers ? analytics.totalUsers.toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Customers</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {analytics?.totalUsers ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    Registered users
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
            
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {topCustomersData.length > 0 ? `₹${(topCustomersData.reduce((sum, customer) => sum + customer.totalPurchase, 0)/100000).toFixed(1)}L` : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Customer Value</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {topCustomersData.length > 0 ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    From top customers
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
          </Box>
        </Box>
      );
    };

    const renderOrderChart = () => {
      // Use real data from analytics state with fallbacks
      const orderAnalytics = analytics?.orders || {};
      
      // Order Status Distribution (Donut Chart) - ONLY USE REAL DATA
      const orderStatusData = analytics?.statusDistribution ? 
        Object.entries(analytics.statusDistribution).map(([status, count], index) => {
          const totalOrders = Object.values(analytics.statusDistribution).reduce((sum, val) => sum + val, 0);
          const percentage = totalOrders > 0 ? ((count / totalOrders) * 100).toFixed(1) : 0;
          const colors = {
            'COMPLETED': '#4caf50',
            'DELIVERED': '#4caf50', 
            'PROCESSING': '#2196f3',
            'CONFIRMED': '#2196f3',
            'PENDING': '#ff9800',
            'CANCELLED': '#f44336',
            'SHIPPED': '#9c27b0',
            'OUT_FOR_DELIVERY': '#00bcd4'
          };
          return {
            status: status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            count: count,
            percentage: parseFloat(percentage),
            color: colors[status] || ['#8884d8', '#82ca9d', '#ffc658', '#ff7300'][index % 4]
          };
        }) : [];

      // Orders by Category (Bar Chart) - ONLY USE REAL DATA
      const ordersByCategoryData = analytics?.categoryBreakdown ? 
        analytics.categoryBreakdown.map(category => ({
          category: category.name,
          orders: Math.floor((category.value / 100) * (analytics?.totalOrders || 0)),
          revenue: category.sales || category.value * 50000,
          percentage: category.value
        })) : productCategories.length > 0 ? 
        productCategories.slice(0, 6).map((category, index) => {
          const totalProducts = productCategories.reduce((sum, cat) => sum + (cat.count || 0), 0);
          const percentage = totalProducts > 0 ? ((category.count || 0) / totalProducts * 100) : 0;
          return {
            category: category.name,
            orders: Math.floor((category.count || 0) * 3), // Estimate orders based on product count
            revenue: (category.count || 0) * 180000, // Estimate revenue
            percentage: percentage.toFixed(1)
          };
        }) : [];

      // Paid vs Pending Orders Comparison (Bar Chart) - ONLY USE REAL DATA
      const paidVsPendingData = monthlyRevenueFromCompletedOrders?.monthlyRevenue ? 
        monthlyRevenueFromCompletedOrders.monthlyRevenue.map(monthData => ({
          month: monthData.month,
          paidOrders: monthData.orders || Math.floor(monthData.revenue / 5000),
          pendingOrders: Math.floor((monthData.orders || Math.floor(monthData.revenue / 5000)) * 0.1),
          paidRevenue: monthData.revenue,
          pendingRevenue: Math.floor(monthData.revenue * 0.1)
        })) : [];

      return (
        <Box sx={{ width: '100%', p: 2 }}>
          <Typography variant="h5" sx={{ 
            mb: 3, 
            textAlign: 'center', 
            fontWeight: 600, 
            color: 'white',
            backgroundColor: '#2C3E50',
            p: 2,
            borderRadius: 1
          }}>
            Order Analytics Dashboard
          </Typography>
          
          {/* First Row: Two charts side by side */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              mb: 3
            }}
          >
            {/* Order Status Distribution (Donut Chart) */}
            <Card sx={{ height: '400px', border: '2px solid #2C3E50' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Order Status Distribution
                  {analytics?.statusDistribution && Object.keys(analytics.statusDistribution).length > 0 && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {analyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <CircularProgress />
                  </Box>
                ) : orderStatusData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No Order Status Data</Typography>
                    <Typography variant="body2" color="text.secondary">Complete some orders to see status distribution</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ status, percentage, cx, cy, midAngle, innerRadius, outerRadius, index }) => {
                        // Only show labels for slices with percentage >= 5% to avoid overcrowding
                        if (percentage < 5) return null;
                        
                        const RADIAN = Math.PI / 180;
                        // Increase radius for better spacing
                        const radius = innerRadius + (outerRadius - innerRadius) * 1.6;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        
                        // Adjust positioning to avoid collisions
                        const adjustedY = y + (index % 2 === 0 ? -5 : 5);
                        
                        return (
                          <text 
                            x={x} 
                            y={adjustedY} 
                            fill="#333" 
                            textAnchor={x > cx ? 'start' : 'end'} 
                            dominantBaseline="central"
                            fontSize="11"
                            fontWeight="600"
                            style={{
                              textShadow: '1px 1px 2px rgba(255,255,255,0.8)',
                              filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.1))'
                            }}
                          >
                            {`${status}: ${percentage}%`}
                          </text>
                        );
                      }}
                      labelLine={false}
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [value.toLocaleString(), 'Orders']}
                      labelFormatter={(label) => `Status: ${label}`}
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #ccc', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '12px' }}
                      formatter={(value, entry) => entry.payload.status}
                    />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Orders by Category (Bar Chart) */}
            <Card sx={{ height: '400px', border: '2px solid #2C3E50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Orders by Category</Typography>
                  {(analyticsLoading || categoriesLoading) && <CircularProgress size={20} />}
                </Box>
                {(analyticsLoading || categoriesLoading) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                    <CircularProgress />
                  </Box>
                ) : ordersByCategoryData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 320, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No Category Data</Typography>
                    <Typography variant="body2" color="text.secondary">Orders by category will appear here</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={ordersByCategoryData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="category" 
                      stroke="#666" 
                      angle={-45} 
                      textAnchor="end" 
                      height={80}
                      interval={0}
                      fontSize={10}
                    />
                    <YAxis yAxisId="left" stroke="#8884d8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'Revenue') return [`₹${(value/100000).toFixed(1)}L`, name];
                        return [value, name];
                      }}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="orders" fill="#8884d8" name="Orders" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="revenue" fill="#82ca9d" name="Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Second Row: One centered chart */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 3,
              mb: 3
            }}
          >
            {/* Paid vs Pending Orders Comparison (Bar Chart) */}
            <Card 
              sx={{
                gridColumn: '1 / 3',
                justifySelf: 'center',
                maxWidth: '50%',
                width: '100%',
                border: '2px solid #2C3E50'
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Paid vs Pending Orders Comparison
                  {monthlyRevenueFromCompletedOrders?.monthlyRevenue && monthlyRevenueFromCompletedOrders.monthlyRevenue.length > 0 && (
                    <Chip 
                      label="Live Data" 
                      color="success" 
                      size="small" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
                {analyticsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <CircularProgress />
                  </Box>
                ) : paidVsPendingData.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400, gap: 2 }}>
                    <Typography variant="h6" color="text.secondary">No Monthly Order Data</Typography>
                    <Typography variant="body2" color="text.secondary">Monthly order comparison will appear here</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={paidVsPendingData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#666" />
                    <YAxis yAxisId="left" stroke="#4caf50" />
                    <YAxis yAxisId="right" orientation="right" stroke="#ff9800" />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name.includes('Revenue')) return [`₹${(value/100000).toFixed(1)}L`, name];
                        return [value, name];
                      }}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="paidOrders" fill="#4caf50" name="Paid Orders" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="pendingOrders" fill="#ff9800" name="Pending Orders" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="paidRevenue" fill="#2e7d32" name="Paid Revenue" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="pendingRevenue" fill="#f57c00" name="Pending Revenue" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Order Metrics Cards */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 2,
              mb: 3
            }}
          >
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {analytics?.totalOrders ? analytics.totalOrders.toLocaleString() : orderStatusData.length > 0 ? orderStatusData.reduce((sum, status) => sum + status.count, 0).toLocaleString() : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Orders</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {analytics?.totalOrders || orderStatusData.length > 0 ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    All orders
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
            
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {analytics?.totalRevenue ? `₹${(analytics.totalRevenue/100000).toFixed(1)}L` : ordersByCategoryData.length > 0 ? `₹${(ordersByCategoryData.reduce((sum, cat) => sum + cat.revenue, 0)/100000).toFixed(1)}L` : 'N/A'}
              </Typography>
              <Typography variant="body2">Total Revenue</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {analytics?.totalRevenue || ordersByCategoryData.length > 0 ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    Total earnings
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
            
            <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {orderStatusData.length > 0 && orderStatusData.find(s => s.status === 'Completed' || s.status === 'Delivered') ? 
                  `${orderStatusData.find(s => s.status === 'Completed' || s.status === 'Delivered')?.percentage || 0}%` : 
                  'N/A'}
              </Typography>
              <Typography variant="body2">Completion Rate</Typography>
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {orderStatusData.length > 0 ? (
                  <>
                    <ArrowUpward sx={{ fontSize: 16, mr: 0.5 }} />
                    Success rate
                  </>
                ) : 'No data available'}
              </Typography>
            </Card>
          </Box>
        </Box>
      );
    };

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          Analytics Dashboard
        </Typography>
        
        {/* CSS Grid Layout for uniform card dimensions */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: 4,
          '@media (min-width: 900px)': {
            gridTemplateColumns: 'repeat(3, 1fr)',
          }
        }}>
          {/* Sales Report Card */}
          <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '400px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  bgcolor: 'success.main', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2
                }}>
                  📊
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Sales Report
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                Comprehensive sales performance analysis including revenue trends, order volumes, and growth metrics over time.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                    ₹{analytics?.totalRevenue?.toLocaleString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                    {analytics?.totalOrders?.toLocaleString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                </Box>
              </Box>
              
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => handleViewAnalytics('sales')}
                sx={{ mt: 'auto' }}
              >
                View Analytics
              </Button>
            </CardContent>
          </Card>

          {/* Customer Report Card */}
          <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '400px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  bgcolor: 'info.main', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2
                }}>
                  👥
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Customer Report
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                Detailed customer analytics including user demographics, seller statistics, and growth patterns across different user segments.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                    {analytics?.totalUsers?.toLocaleString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Users</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 600 }}>
                    {analytics?.totalSellers?.toLocaleString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Sellers</Typography>
                </Box>
              </Box>
              
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => handleViewAnalytics('customer')}
                sx={{ mt: 'auto' }}
              >
                View Analytics
              </Button>
            </CardContent>
          </Card>

          {/* Order Report Card */}
          <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: '400px',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
            }
          }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  bgcolor: 'warning.main', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 48, 
                  height: 48, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2
                }}>
                  📦
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Order Report
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                In-depth order analysis covering payment statuses, order fulfillment rates, average order values, and transaction success metrics.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                    ₹{analytics?.avgOrderValue?.toLocaleString() || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Avg Order Value</Typography>
                </Box>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                    {analytics?.successRate?.toFixed(1) || '0'}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                </Box>
              </Box>
              
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => handleViewAnalytics('order')}
                sx={{ mt: 'auto' }}
              >
                View Analytics
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Analytics Dialog */}
        <Dialog
          open={analyticsDialogOpen}
          onClose={() => setAnalyticsDialogOpen(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              maxHeight: '95vh',
              m: 1
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'linear-gradient(135deg,#2C3E50,#2C3E50)', 
            background: 'linear-gradient(135)',
            color: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 3,
            borderRadius: '12px 12px 0 0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                bgcolor: '#2C3E50', 
                borderRadius: '50%', 
                p: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                📈
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {selectedAnalytics === 'sales' ? 'Sales Analytics Dashboard' : 
                   selectedAnalytics === 'customer' ? 'Customer Analytics Dashboard' : 
                   'Order Analytics Dashboard'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Comprehensive insights and performance metrics
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => setAnalyticsDialogOpen(false)}
              sx={{ 
                color: '#2C3E50', 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            >
              ✕
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ 
            p: 0, 
            bgcolor: '#f8fafc',
            minHeight: '70vh',
            overflow: 'auto'
          }}>
            <Box sx={{ 
              p: 3,
              background: 'linear-gradient(180deg, rgba(102, 126, 234, 0.05) 0%, rgba(248, 250, 252, 1) 100%)'
            }}>
              {selectedAnalytics === 'sales' && renderSalesChart()}
              {selectedAnalytics === 'customer' && renderCustomerChart()}
              {selectedAnalytics === 'order' && renderOrderChart()}
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    );
  };
  
  const VisitRequestsTab = () => (
    <Box>
      <VisitRequestsPage />
    </Box>
  );
  
  const SettingsTab = () => {
    const [settingsTab, setSettingsTab] = useState(0);
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 3 }}>
          System Settings
        </Typography>
        
        <Tabs 
          value={settingsTab} 
          onChange={(e, newValue) => setSettingsTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Banner Management" />
          <Tab label="System Configuration" />
          <Tab label="User Management" />
          <Tab label="Reported and Blocked" />
          <Tab label="Platform Settings" />
        </Tabs>
        
        {settingsTab === 0 && (
          <Box>
            <BannerManagement />
          </Box>
        )}
        
        {settingsTab === 1 && (
          <Box>
            <Alert severity="info">
              System configuration panel is coming soon. This will include general system settings and configurations.
            </Alert>
          </Box>
        )}
        
        {settingsTab === 2 && (
          <Box>
            <Alert severity="info">
              User management panel is coming soon. This will include user roles, permissions, and account management.
            </Alert>
          </Box>
        )}
        
        {settingsTab === 3 && (
          <Box>
            <ReportedAndBlocked />
          </Box>
        )}

        {settingsTab === 4 && (
          <Box>
            <Alert severity="info">
              Platform settings panel is coming soon. This will include marketplace policies, payment settings, and other platform configurations.
            </Alert>
          </Box>
        )}
      </Box>
    );
  };
  
  const drawerWidth = sidebarCollapsed ? 80 : 280;
  
  // Logo and Welcome Section Component
  const CombinedLogoWelcomeSection = ({ collapsed }) => (
    <Box sx={{ 
      p: collapsed ? 1.5 : 3, 
      borderBottom: '1px solid #e0e0e0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      minHeight: collapsed ? '80px' : '140px'
    }}>
      {/* Logo Section */}
      <Box
        component={RouterLink}
        to="/"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textDecoration: 'none',
          mb: collapsed ? 0 : 2,
          width: '100%'
        }}
      >
        <Box
          component="img"
          src="/mos-logo.jpg"
          alt="MOS Logo"
          sx={{
            width: collapsed ? 35 : 50,
            height: collapsed ? 35 : 50,
            borderRadius: '12px',
            objectFit: 'cover',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            border: '2px solid rgba(0,0,0,0.2)',
            transform: collapsed ? 'scale(1)' : 'scale(1.4)',
            cursor: 'pointer',
            '&:hover': {
              transform: collapsed ? 'scale(1.05)' : 'scale(1.07)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
              border: '2px solid rgba(0,0,0,0.4)',
            }
          }}
        />
      </Box>
      
      {/* Logo Text and Welcome Section */}
      {!collapsed && (
        <Box sx={{
          opacity: 1,
          transform: 'translateY(0)',
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.1s",
          textAlign: 'center'
        }}>
          {/* Brand Name */}
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: "black",
            letterSpacing: "0.5px",
            lineHeight: 1.2,
            fontSize: '1.25rem',
            mb: 0.5
          }}>
            Mall of Stone
          </Typography>
          
          {/* Portal Label */}
          <Typography variant="caption" sx={{ 
            color: 'rgba(0,0,0,0.7)',
            fontWeight: 500,
            letterSpacing: '0.5px',
            fontSize: '0.75rem',
            display: 'block',
            mb: 1
          }}>
            ADMIN PORTAL
          </Typography>
        </Box>
      )}

      {/* Welcome Text */}
      {!collapsed && (
        <Typography
          variant="body2"
          sx={{ 
            color: "rgba(0,0,0,0.9)", 
            fontWeight: 600,
            fontSize: '0.9rem',
            mb: 1,
            textAlign: 'center'
          }}
        >
          Welcome, {user?.firstName || 'Admin'}!
        </Typography>
      )}

      {/* Collapsed state - show only logo text */}
      {collapsed && (
        <Typography variant="caption" sx={{ 
          fontWeight: 600, 
          color: "black",
          letterSpacing: "0.3px",
          fontSize: '0.7rem',
          mt: 0.5,
          textAlign: 'center',
          display: 'block',
          width: '100%'
        }}>
          MOS
        </Typography>
      )}
    </Box>
  );

  const Sidebar = () => (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          background: 'white',
          color: 'black',
          borderRight: '1px solid #e0e0e0',
          transition: 'width 0.3s ease',
        },
      }}
    >
      {/* Combined Logo and Welcome Section */}
      <CombinedLogoWelcomeSection collapsed={sidebarCollapsed} />

      {/* Sidebar Toggle Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: sidebarCollapsed ? 'center' : 'flex-end', 
        p: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <IconButton
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          sx={{
            color: 'black',
            backgroundColor: 'rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.2)',
            width: 32,
            height: 32,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              border: '1px solid rgba(0,0,0,0.4)',
            },
          }}
        >
          {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
        </IconButton>
      </Box>
      
      <List sx={{ pt: 2, px: 1 }}>
        {sidebarItems.map((item) => (
          <MuiTooltip
            key={item.value}
            title={sidebarCollapsed ? item.label : ""}
            placement="right"
            arrow
            sx={{
              "& .MuiTooltip-tooltip": {
                bgcolor: "rgba(0,0,0,0.9)",
                color: "white",
                borderRadius: "8px",
                fontSize: "0.8rem",
                fontWeight: 500,
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                backdropFilter: "blur(10px)"
              },
              "& .MuiTooltip-arrow": {
                color: "rgba(0,0,0,0.9)"
              }
            }}
          >
            <ListItemButton
              onClick={() => setActiveTab(item.value)}
              sx={{
                mb: 0.25,
                mx: sidebarCollapsed ? 0.5 : 0,
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                minHeight: '40px',
                maxHeight: '40px',
                backgroundColor: activeTab === item.value
                  ? "rgba(0,0,0,0.15)"
                  : "transparent",
                border: activeTab === item.value 
                  ? "1px solid rgba(0,0,0,0.2)" 
                  : "1px solid transparent",
                boxShadow: activeTab === item.value 
                  ? "0 2px 8px rgba(0,0,0,0.1), inset 0 1px 0 rgba(0,0,0,0.2)" 
                  : "none",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                px: sidebarCollapsed ? 1 : '8px 20px',
                py: '8px',
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                // Active indicator line
                "&::before": {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: activeTab === item.value ? '3px' : '0px',
                  background: 'linear-gradient(180deg, #000000 0%, rgba(0,0,0,0.8) 100%)',
                  borderRadius: '0 3px 3px 0',
                  transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: activeTab === item.value 
                  ? "#000000" 
                  : "rgba(0,0,0,0.6)",
                minWidth: sidebarCollapsed ? 'auto' : 36,
                justifyContent: 'center',
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: activeTab === item.value ? 'scale(1.05)' : 'scale(1)',
                filter: activeTab === item.value 
                  ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                  : 'none',
                '&:hover': {
                  color: "rgba(0,0,0,0.9)",
                  transform: 'scale(1.02)',
                },
                '& svg': {
                  fontSize: '20px'
                }
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: activeTab === item.value ? 600 : 500,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                  color: activeTab === item.value 
                    ? "#000000" 
                    : "rgba(0,0,0,0.8)",
                }}
                sx={{
                  opacity: sidebarCollapsed ? 0 : 1,
                  transition: "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  ml: sidebarCollapsed ? 0 : 1,
                }}
              />
            </ListItemButton>
          </MuiTooltip>
        ))}
      </List>
    </Drawer>
  );
  
  if (!isAdminUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to access the admin dashboard.</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <Sidebar />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: isMobile ? '100%' : sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: isMobile ? 0 : sidebarOpen ? 0 : `-${drawerWidth}px`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        {/* Top App Bar */}
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            bgcolor: 'white', 
            color: 'text.primary',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              sx={{ mr: 2 }}
            >
              {sidebarOpen ? <ChevronLeft /> : <MenuIcon />}
            </IconButton>
            
            <Breadcrumbs sx={{ flexGrow: 1 }}>
              <Link component={RouterLink} to="/" color="inherit">
                <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                Home
              </Link>
              <Typography color="text.primary">
                {sidebarItems.find(item => item.value === activeTab)?.label || 'Dashboard'}
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Time Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                </Select>
              </FormControl>
              
              <IconButton onClick={() => toast.info('Data refreshed!')}>
                <Refresh />
              </IconButton>
              
              {/* <Badge badgeContent={4} color="error">
                <IconButton>
                  <Notifications />
                </IconButton>
              </Badge> */}
            </Box>
          </Toolbar>
        </AppBar>
        
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header - Only show on Overview tab */}
          {activeTab === 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                Admin Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Welcome back, {user?.firstName || 'Admin'}! Here's what's happening with your marketplace.
              </Typography>
            </Box>
          )}
      
      {/* Quick Actions - Only show on Overview tab */}
      {activeTab === 0 && (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item>
            <Badge badgeContent={dashboardStats.pendingApprovals.value} color="error">
              <Button variant="outlined" startIcon={<People />} onClick={handlePendingApprovals}>
                Pending Approvals
              </Button>
            </Badge>
          </Grid>
          <Grid item>
            <Badge badgeContent={dashboardStats.activeDisputes.value} color="warning">
              <Button variant="outlined" startIcon={<Warning />} onClick={handleActiveDisputes}>
                Active Disputes
              </Button>
            </Badge>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<Assessment />} onClick={handleGenerateReport}>
              Generate Report
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<Security />} onClick={handleSecurityLogs}>
              Audit Logs
            </Button>
          </Grid>
        </Grid>
      )}
      
      {/* Content Area */}
      <Paper sx={{ width: '100%', p: 3 }}>
        {/* Error Handling Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(clearAdminError())}>
            <Typography variant="body2">
              <strong>Data Loading Error:</strong> {error}
            </Typography>
          </Alert>
        )}
        
        {/* Analytics Data Error */}
        {!analyticsLoading && !analytics && activeTab === 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => {
                  dispatch(fetchAdminAnalytics());
                  fetchProductCategories();
                }}
                disabled={analyticsLoading || categoriesLoading}
              >
                {analyticsLoading || categoriesLoading ? 'Retrying...' : 'Retry'}
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Analytics Unavailable:</strong> Some dashboard statistics may not be current. Please refresh the page or try again later.
            </Typography>
          </Alert>
        )}
        
        {/* Monthly Revenue Data Error */}
        {!monthlyRevenueFromCompletedOrdersLoading && !monthlyRevenueFromCompletedOrders && activeTab === 7 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => dispatch(fetchMonthlyRevenueFromCompletedOrders(new Date().getFullYear()))}
                disabled={monthlyRevenueFromCompletedOrdersLoading}
              >
                {monthlyRevenueFromCompletedOrdersLoading ? 'Retrying...' : 'Retry'}
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Financial Data Unavailable:</strong> Revenue and financial statistics may not be current. Please refresh the page or try again later.
            </Typography>
          </Alert>
        )}
        
        {activeTab === 0 && <OverviewTab />}
              {activeTab === 1 && <SellersTab />}
              {activeTab === 2 && (
                <ProductsTabContent
                  pendingCount={pendingProductsCount}
                  pendingProductsLoading={pendingProductsLoading}
                  approvedProducts={adminApprovedProducts || []}
                  approvedProductsLoading={adminApprovedProductsLoading}
                  rejectedProducts={adminRejectedProducts || []}
                  rejectedProductsLoading={adminRejectedProductsLoading}
                  error={error}
                  sortedProducts={sortedPendingProducts}
                  onRefresh={handleRefreshData}
                  onSort={handleSort}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onViewProduct={handleViewProduct}
                  onApproveProduct={handleApproveProduct}
                  onRejectProduct={openProductRejectionDialog}
                  adminLoading={adminLoading}
                  localLoading={localLoading}
                  tagFilter={tagFilter}
                  onTagFilterChange={setTagFilter}
                />
              )}
              {activeTab === 3 && <FeaturedProductsTab />}
              {activeTab === 4 && <AdminOrdersPage />}
              {activeTab === 5 && <VisitRequestsTab />}
              {activeTab === 6 && <AdminLotRequestsPage />}
              {activeTab === 7 && <FinancialTab />}
              {activeTab === 8 && <AnalyticsTab />}
              {activeTab === 9 && <SettingsTab />}
              {activeTab === 10 && (
                <PendingOrderApprovalsTab
                  onApproveOrder={handleApproveOrder}
                  onRejectOrder={handleRejectOrder}
                  pendingOrders={pendingOrderApprovals}
                  loading={pendingOrderApprovalsLoading || localLoading}
                  page={orderApprovalPage}
                  totalPages={orderApprovalTotalPages}
                  totalCount={orderApprovalTotalCount}
                  onPageChange={setOrderApprovalPage}
                  onRefresh={loadPendingOrderApprovals}
                  sortField={orderSortField}
                  sortDirection={orderSortDirection}
                  onSort={(field, direction) => {
                    setOrderSortField(field);
                    setOrderSortDirection(direction);
                    // Reload with new sort
                    loadPendingOrderApprovals();
                  }}
                />
              )}
      </Paper>
      
      {/* Rejection Confirmation Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Seller Application</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reject the application from {selectedSeller?.businessProfile?.businessName || `${selectedSeller?.firstName} ${selectedSeller?.lastName}`}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for rejection"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this seller application..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmRejectSeller}
            disabled={!rejectionReason.trim() || localLoading}
          >
            {localLoading ? 'Rejecting...' : 'Reject Seller'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Rejection Dialog */}
      <Dialog
        open={productRejectionDialogOpen}
        onClose={closeProductRejectionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
          }}
        >
          <Cancel color="error" sx={{ fontSize: 28 }} />
          Reject Product Listing
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                Product
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, color: 'text.primary' }}>
                {productRejectionTarget?.name || 'Unknown Product'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Seller: {productRejectionTarget?.sellerName || 'N/A'}
              </Typography>
            </Box>

            <TextField
              label="Reason for rejection"
              value={productRejectionReason}
              onChange={(e) => {
                setProductRejectionReason(e.target.value);
                if (productRejectionError) {
                  setProductRejectionError('');
                }
              }}
              multiline
              minRows={4}
              placeholder="Clearly explain why this product is being rejected. The seller will see this message."
              fullWidth
              error={Boolean(productRejectionError)}
              helperText={productRejectionError || 'Provide actionable feedback to help the seller fix the issues.'}
            />

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              The seller will be notified immediately and the product will move to their rejected list. They can edit and resubmit after addressing your feedback.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <Button onClick={closeProductRejectionDialog} disabled={localLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmProductRejection}
            disabled={localLoading}
            startIcon={<Cancel />}
          >
            {localLoading ? 'Rejecting...' : 'Reject Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Seller Details Dialog */}
      <Dialog
        open={sellerDialogOpen}
        onClose={() => setSellerDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2C3E50 0%, #2C3E50 100%)',
          color: 'white',
          textAlign: 'center',
          py: 3,
          fontSize: '1.5rem',
          fontWeight: 600
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <Person sx={{ fontSize: 28 }} />
            Seller Application Details
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedSeller && (
            <Box>
              {/* Header Section */}
              <Box sx={{ 
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                p: 3,
                borderBottom: '1px solid #e0e0e0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
                      {selectedSeller.businessProfile?.businessName || `${selectedSeller.firstName} ${selectedSeller.lastName}`}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
                      Application submitted on {selectedSeller.createdAt ? new Date(selectedSeller.createdAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) : 'N/A'}
                    </Typography>
                  </Box>
                  <Chip 
                    label={selectedSeller.businessProfile?.approvalStatus || 'PENDING'} 
                    color={selectedSeller.businessProfile?.approvalStatus === 'APPROVED' ? 'success' : 'warning'}
                    size="large"
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      px: 2,
                      py: 1,
                      height: 'auto'
                    }}
                  />
                </Box>
              </Box>

              {/* Content Sections */}
              <Box sx={{ p: 3 }}>
                {/* Personal Information */}
                <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      color: '#2c3e50', 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <ContactMail sx={{ color: '#3498db' }} />
                      Personal Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 2,
                          border: '1px solid #e9ecef'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Email Address
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                            {selectedSeller.email}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 2,
                          border: '1px solid #e9ecef'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Phone Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                            {selectedSeller.phoneNumber || 'Not provided'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Business Information */}
                <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ 
                      mb: 2, 
                      color: '#2c3e50', 
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <Business sx={{ color: '#e74c3c' }} />
                      Business Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 2,
                          border: '1px solid #e9ecef'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Business Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                            {selectedSeller.businessProfile?.businessType || selectedSeller.businessType || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 2,
                          border: '1px solid #e9ecef'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            GST Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                            {selectedSeller.businessProfile?.gstNumber || selectedSeller.gstNumber || 'Not provided'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ 
                          p: 2, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 2,
                          border: '1px solid #e9ecef'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                            PAN Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                            {selectedSeller.businessProfile?.panNumber || 'Not provided'}
                          </Typography>
                        </Box>
                      </Grid>
                      {selectedSeller.submittedAt && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: 2,
                            border: '1px solid #e9ecef'
                          }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                              Submission Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                              {selectedSeller.submittedAt}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Address Information */}
                {selectedSeller.businessProfile?.businessAddress && (
                  <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ 
                        mb: 2, 
                        color: '#2c3e50', 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <LocationOn sx={{ color: '#f39c12' }} />
                        Business Address
                      </Typography>
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: 2,
                        border: '1px solid #e9ecef'
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50', lineHeight: 1.6 }}>
                          {selectedSeller.businessProfile.businessAddress.street}<br />
                          {selectedSeller.businessProfile.businessAddress.city}, {selectedSeller.businessProfile.businessAddress.state}<br />
                          PIN: {selectedSeller.businessProfile.businessAddress.postalCode}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {/* Bank Details */}
                {selectedSeller.businessProfile?.bankDetails && (
                  <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{
                        mb: 2,
                        color: '#2c3e50',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <AccountBalance sx={{ color: '#27ae60' }} />
                        Bank Details
                      </Typography>
                      <Grid container spacing={3}>
                        {selectedSeller.businessProfile.bankDetails.accountHolderName && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e9ecef'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Account Holder Name
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                {selectedSeller.businessProfile.bankDetails.accountHolderName}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedSeller.businessProfile.bankDetails.accountNumber && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e9ecef'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Account Number
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                {selectedSeller.businessProfile.bankDetails.accountNumber}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedSeller.businessProfile.bankDetails.ifscCode && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e9ecef'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                IFSC Code
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                {selectedSeller.businessProfile.bankDetails.ifscCode}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedSeller.businessProfile.bankDetails.bankName && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e9ecef'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Bank Name
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                {selectedSeller.businessProfile.bankDetails.bankName}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedSeller.businessProfile.bankDetails.branchName && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e9ecef'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Branch Name
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                {selectedSeller.businessProfile.bankDetails.branchName}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {selectedSeller.businessProfile.bankDetails.accountType && (
                          <Grid item xs={12} md={6}>
                            <Box sx={{
                              p: 2,
                              backgroundColor: '#f8f9fa',
                              borderRadius: 2,
                              border: '1px solid #e9ecef'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Account Type
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                {selectedSeller.businessProfile.bankDetails.accountType}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Documents */}
                {selectedSeller?.documents && selectedSeller.documents.length > 0 && (
                  <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ 
                        mb: 2, 
                        color: '#2c3e50', 
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Description sx={{ color: '#9b59b6' }} />
                        Submitted Documents
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {selectedSeller.documents.map((doc, index) => (
                          <Chip 
                            key={index} 
                            label={doc} 
                            variant="outlined"
                            sx={{
                              borderColor: '#9b59b6',
                              color: '#9b59b6',
                              fontWeight: 500,
                              '&:hover': {
                                backgroundColor: 'rgba(155, 89, 182, 0.1)'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
          gap: 2
        }}>
          <Button 
            onClick={() => setSellerDialogOpen(false)}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600
            }}
          >
            Close
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<Cancel />}
            onClick={() => {
              setSellerDialogOpen(false);
              handleRejectSeller(selectedSeller);
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600
            }}
          >
            Reject
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<CheckCircle />}
            onClick={() => {
              setSellerDialogOpen(false);
              handleApproveSeller(selectedSeller);
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Enhanced Product Details Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 2
        }}>
          <Box sx={{ 
            bgcolor: 'rgba(255,255,255,0.2)', 
            borderRadius: '50%', 
            p: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <Visibility />
          </Box>
          <Box>
            <Typography variant="h6" component="div">Product Details</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Review and approve product listing
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {selectedProduct && (
            <Box>
              {/* Header Section */}
              <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                      {selectedProduct.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <Chip 
                        label={selectedProduct.category} 
                        color="primary" 
                        variant="outlined"
                        size="small"
                      />
                      <Chip 
                        label={`₹${selectedProduct.price?.toLocaleString()}`} 
                        color="success" 
                        variant="filled"
                        size="small"
                      />
                      <Chip 
                        label={selectedProduct.approvalStatus || 'PENDING'} 
                        color="warning" 
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary">Submitted</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedProduct.submittedAt || selectedProduct.createdAt || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Main Content */}
              <Box sx={{ p: 3 }}>
                <Grid container spacing={4}>
                  {/* Left Column - Images */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: 24, 
                          height: 24, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          📷
                        </Box>
                        Product Images
                      </Typography>
                      
                      {(() => {
                        const images = getProductImageUrls(selectedProduct);
                        const primaryImage = getProductPrimaryImageUrl(selectedProduct);
                        
                        if (images.length === 0 && !primaryImage) {
                          return (
                            <Box sx={{ 
                              width: '100%',
                              height: 250, 
                              bgcolor: 'grey.100', 
                              display: 'flex', 
                              flexDirection: 'column',
                              alignItems: 'center', 
                              justifyContent: 'center',
                              borderRadius: 2,
                              border: '2px dashed',
                              borderColor: 'grey.300'
                            }}>
                              <Box sx={{ fontSize: '3rem', mb: 1 }}>🏗️</Box>
                              <Typography variant="body2" color="text.secondary">
                                No images available
                              </Typography>
                            </Box>
                          );
                        }
                        
                        const allImages = images.length > 0 ? images : [primaryImage];
                        
                        return (
                          <Box>
                            {/* Primary Image */}
                            <Box
                              sx={{
                                mb: 2,
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid #e0e0e0',
                                cursor: 'zoom-in',
                              }}
                              onClick={() =>
                                openImagePreview(
                                  allImages[0] || '/placeholder-image.jpg',
                                  `${selectedProduct.name || 'Product'} primary image`
                                )
                              }
                            >
                              <Box
                                component="img"
                                src={allImages[0] || '/placeholder-image.jpg'}
                                alt={`${selectedProduct.name} primary`}
                                sx={{
                                  width: '100%',
                                  height: 250,
                                  objectFit: 'cover',
                                  display: 'block',
                                  transition: 'transform 0.3s ease',
                                  transformOrigin: 'center',
                                  '&:hover': {
                                    transform: 'scale(1.9)',
                                  },
                                }}
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder-image.jpg';
                                }}
                              />
                            </Box>
                            
                            {/* Additional Images */}
                            {allImages.length > 1 && (
                              <Grid container spacing={1}>
                                {allImages.slice(1).map((imageUrl, index) => (
                                  <Grid item xs={4} key={index + 1}>
                                    <Box
                                      sx={{
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        border: '1px solid #e0e0e0',
                                        cursor: imageUrl ? 'zoom-in' : 'default',
                                      }}
                                      onClick={() =>
                                        imageUrl &&
                                        openImagePreview(
                                          imageUrl,
                                          `${selectedProduct.name || 'Product'} image ${index + 2}`
                                        )
                                      }
                                    >
                                      <Box
                                        component="img"
                                        src={imageUrl || '/placeholder-image.jpg'}
                                        alt={`${selectedProduct.name} ${index + 2}`}
                                        sx={{
                                          width: '100%',
                                          height: 80,
                                          objectFit: 'cover',
                                          display: 'block',
                                          transition: imageUrl ? 'transform 0.3s ease' : 'none',
                                          transformOrigin: 'center',
                                          '&:hover': {
                                            transform: imageUrl ? 'scale(1.9)' : 'none',
                                          },
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.src = '/placeholder-image.jpg';
                                        }}
                                      />
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </Box>
                        );
                      })()}
                    </Paper>

                    {/* Product Videos Section */}
                    {selectedProduct.videoUrls && selectedProduct.videoUrls.length > 0 && (
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            bgcolor: 'primary.main', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: 24, 
                            height: 24, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>
                            🎥
                          </Box>
                          Product Videos
                        </Typography>
                        <Grid container spacing={2}>
                          {selectedProduct.videoUrls.map((videoUrl, index) => {
                            const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
                            // Videos are stored in /uploads/products/ but served via /api/files/images/products/
                            let fullVideoUrl = videoUrl;
                            if (typeof videoUrl === 'string' && !videoUrl.startsWith('http')) {
                              // If it's a relative path, construct the full URL
                              if (videoUrl.startsWith('/api/files/')) {
                                fullVideoUrl = `${API_BASE.replace('/api', '')}${videoUrl}`;
                              } else if (videoUrl.startsWith('/uploads/products/')) {
                                // Convert /uploads/products/filename to /api/files/images/products/filename
                                const filename = videoUrl.replace('/uploads/products/', '');
                                fullVideoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${filename}`;
                              } else {
                                // Assume it's a filename in the products directory
                                fullVideoUrl = `${API_BASE.replace('/api', '')}/api/files/images/products/${videoUrl}`;
                              }
                            }
                            return (
                              <Grid item xs={12} key={index}>
                                <Box
                                  sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '1px solid #e0e0e0',
                                  }}
                                >
                                  <Box
                                    component="video"
                                    src={fullVideoUrl}
                                    controls
                                    sx={{
                                      width: '100%',
                                      maxHeight: 300,
                                      objectFit: 'contain',
                                      display: 'block',
                                      bgcolor: '#000',
                                    }}
                                  />
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Paper>
                    )}
                  </Grid>

                  {/* Right Column - Details */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* Basic Information */}
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            bgcolor: 'info.main', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: 24, 
                            height: 24, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>
                            ℹ️
                          </Box>
                          Basic Information
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Seller</Typography>
                            <Typography variant="body1">{selectedProduct.seller || selectedProduct.sellerName || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Category</Typography>
                            <Typography variant="body1">{selectedProduct.category || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Sub Category</Typography>
                            <Typography variant="body1">{selectedProduct.subCategory || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Stock Quantity</Typography>
                            <Typography variant="body1">{selectedProduct.stockQuantity || selectedProduct.stock || 'N/A'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Max Order Qty</Typography>
                            <Typography variant="body1">{selectedProduct.maximumOrderQuantity || 'N/A'}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Specifications */}
                      {selectedProduct.specifications && (
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                              bgcolor: 'secondary.main', 
                              color: 'white', 
                              borderRadius: '50%', 
                              width: 24, 
                              height: 24, 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center',
                              fontSize: '12px'
                            }}>
                              🔧
                            </Box>
                            Specifications
                          </Typography>
                          
                          <Grid container spacing={2}>
                            {selectedProduct.specifications.material && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Material</Typography>
                                <Typography variant="body1">{selectedProduct.specifications.material}</Typography>
                              </Grid>
                            )}
                            {selectedProduct.specifications.finish && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Finish</Typography>
                                <Typography variant="body1">{selectedProduct.specifications.finish}</Typography>
                              </Grid>
                            )}
                            {selectedProduct.specifications.color && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Color</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    bgcolor: selectedProduct.specifications.color.toLowerCase(),
                                    border: '1px solid #ddd'
                                  }} />
                                  <Typography variant="body1">{selectedProduct.specifications.color}</Typography>
                                </Box>
                              </Grid>
                            )}
                            {selectedProduct.specifications.pattern && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Pattern</Typography>
                                <Typography variant="body1">{selectedProduct.specifications.pattern}</Typography>
                              </Grid>
                            )}
                            {(selectedProduct.specifications.length || selectedProduct.specifications.width || selectedProduct.specifications.thickness) && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Dimensions</Typography>
                                <Typography variant="body1">
                                  {[
                                    selectedProduct.specifications.length && `L: ${selectedProduct.specifications.length}mm`,
                                    selectedProduct.specifications.width && `W: ${selectedProduct.specifications.width}mm`,
                                    selectedProduct.specifications.thickness && `T: ${selectedProduct.specifications.thickness}mm`
                                  ].filter(Boolean).join(' × ') || 'N/A'}
                                </Typography>
                              </Grid>
                            )}
                            {selectedProduct.specifications.weight && (
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Weight</Typography>
                                <Typography variant="body1">{selectedProduct.specifications.weight} kg</Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      )}
                    </Box>
                  </Grid>

                  {/* Full Width Sections */}
                  <Grid item xs={12}>
                    {/* Description */}
                    {selectedProduct.description && (
                      <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            bgcolor: 'success.main', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: 24, 
                            height: 24, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>
                            📝
                          </Box>
                          Description
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          {selectedProduct.description}
                        </Typography>
                      </Paper>
                    )}

                    {/* Additional Details */}
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ 
                          bgcolor: 'warning.main', 
                          color: 'white', 
                          borderRadius: '50%', 
                          width: 24, 
                          height: 24, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '12px'
                        }}>
                          📋
                        </Box>
                        Additional Information
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Origin</Typography>
                          <Typography variant="body1">{selectedProduct.origin || selectedProduct.specifications?.origin || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Featured</Typography>
                          <Chip 
                            label={selectedProduct.featured ? 'Yes' : 'No'} 
                            color={selectedProduct.featured ? 'success' : 'default'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Sponsored</Typography>
                          <Chip 
                            label={selectedProduct.sponsored ? 'Yes' : 'No'} 
                            color={selectedProduct.sponsored ? 'warning' : 'default'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Product ID</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {selectedProduct.id}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'grey.200' }}>
          <Button 
            onClick={() => setProductDialogOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Close
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<Cancel />}
            onClick={() => {
              const product = selectedProduct;
              setProductDialogOpen(false);
              if (product) {
                openProductRejectionDialog(product);
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Reject
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<CheckCircle />}
            onClick={() => {
              setProductDialogOpen(false);
              handleApproveProduct(selectedProduct.id);
            }}
            sx={{ minWidth: 120 }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Approval Confirmation Dialog */}
      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Seller Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to approve {selectedSeller?.name}? This action will grant them access to sell on the platform.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={confirmApproval}>
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Rejection Confirmation Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Seller Application</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Please provide a reason for rejecting {selectedSeller?.name}'s application:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRejection}>
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pending Approvals Dialog */}
      <Dialog
        open={pendingApprovalsDialogOpen}
        onClose={() => setPendingApprovalsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Group color="primary" />
            Pending Approvals
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={pendingApprovalsTab} onChange={(e, newValue) => setPendingApprovalsTab(newValue)}>
              <Tab label={`Sellers (${getPendingSellersArray().length})`} value="sellers" />
              <Tab label={`Products (${getPendingProductsArray().length})`} value="products" />
            </Tabs>
          </Box>

          {pendingApprovalsTab === 'sellers' && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSellerSort('businessName')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Business Name
                        {sellerSortField === 'businessName' && (
                          sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSellerSort('businessType')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Type
                        {sellerSortField === 'businessType' && (
                          sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSellerSort('gstNumber')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        GST Number
                        {sellerSortField === 'gstNumber' && (
                          sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSellerSort('createdAt')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Submitted
                        {sellerSortField === 'createdAt' && (
                          sellerSortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedSellers(getPendingSellersArray()).map((seller) => (
                    <TableRow key={seller.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {seller.businessProfile?.businessName || `${seller.firstName} ${seller.lastName}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {seller.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={seller.businessProfile?.businessType || 'N/A'} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell>{seller.businessProfile?.gstNumber || 'N/A'}</TableCell>
                      <TableCell>
                        {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={seller.businessProfile ? '1 profile' : '0 docs'}
                          size="small"
                          color={seller.businessProfile ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => {
                              setSelectedSeller(seller);
                              setSellerDialogOpen(true);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircle />}
                            onClick={() => handleApprove('seller', seller.id)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => handleReject('seller', seller.id)}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pendingApprovalsTab === 'products' && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Product Name
                        {sortField === 'name' &&
                          (sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sellerName')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Seller
                        {sortField === 'sellerName' &&
                          (sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('category')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Category
                        {sortField === 'category' &&
                          (sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('price')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Price
                        {sortField === 'price' &&
                          (sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('createdAt')}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Submitted
                        {sortField === 'createdAt' &&
                          (sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                      </Box>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedProducts(getPendingProductsArray()).map((product) => {
                    const submissionTag = getProductSubmissionType(product);
                    const tagConfig = {
                      new: { label: 'New Product', color: '#2196F3', bgColor: '#E3F2FD' },
                      edited: { label: 'Edited Product', color: '#FF9800', bgColor: '#FFF3E0' },
                      resubmitted: { label: 'Resubmitted Product', color: '#9C27B0', bgColor: '#F3E5F5' }
                    };
                    const tag = tagConfig[submissionTag] || tagConfig.new;
                    
                    return (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Avatar
                            src={getProductPrimaryImageUrl(product)}
                            alt={product.name}
                            variant="rounded"
                            sx={{ width: 60, height: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography variant="subtitle2">{product.name}</Typography>
                            <Chip
                              label={tag.label}
                              size="small"
                              sx={{
                                backgroundColor: tag.bgColor,
                                color: tag.color,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: '20px',
                                width: 'fit-content'
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{product.sellerName || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip label={product.category} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>₹{product.price}</TableCell>
                        <TableCell>{product.createdAt ? new Date(product.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" startIcon={<Visibility />} onClick={() => handleViewProduct(product)}>
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleApprove('product', product.id)}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => openProductRejectionDialog(product)}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingApprovalsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Order View Dialog for Pending Approvals */}
      <Dialog open={orderViewDialogOpen} onClose={() => setOrderViewDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Order Details - {selectedOrderForView?.orderNumber}</Typography>
            <Chip 
              label={selectedOrderForView?.status || 'PENDING'} 
              color="warning" 
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOrderForView && (
            <Box sx={{ mt: 2 }}>
              {/* Customer Information Section */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader 
                  title="Customer Information" 
                  avatar={
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <Person sx={{ fontSize: 28 }} />
                    </Avatar>
                  }
                  sx={{ bgcolor: 'primary.light', '& .MuiCardHeader-title': { color: 'primary.dark', fontWeight: 600 } }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Customer Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedOrderForView.customerName || 'Guest'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {selectedOrderForView.customerEmail || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">
                        {selectedOrderForView.customerPhone || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Order Date</Typography>
                      <Typography variant="body1">
                        {selectedOrderForView.createdAt ? new Date(selectedOrderForView.createdAt).toLocaleString() : 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Shipping Address Section */}
              {selectedOrderForView.shippingAddress && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardHeader 
                    title="Shipping Address" 
                    avatar={
                      <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                        <LocationOn sx={{ fontSize: 28 }} />
                      </Avatar>
                    }
                    sx={{ bgcolor: 'success.light', '& .MuiCardHeader-title': { color: 'success.dark', fontWeight: 600 } }}
                  />
                  <CardContent>
                    <Typography variant="body1">
                      {selectedOrderForView.shippingAddress.street || ''}
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrderForView.shippingAddress.city || ''}, {selectedOrderForView.shippingAddress.state || ''}
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrderForView.shippingAddress.postalCode || selectedOrderForView.shippingAddress.zipCode || ''} - {selectedOrderForView.shippingAddress.country || 'India'}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Order Items Section */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader 
                  title={`Order Items (${selectedOrderForView.items?.length || 0})`}
                  avatar={
                    <Avatar sx={{ bgcolor: 'warning.main', width: 48, height: 48 }}>
                      <ShoppingCart sx={{ fontSize: 28 }} />
                    </Avatar>
                  }
                  sx={{ bgcolor: 'warning.light', '& .MuiCardHeader-title': { color: 'warning.dark', fontWeight: 600 } }}
                />
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Seller</TableCell>
                          <TableCell>Quantity</TableCell>
                          <TableCell>Unit Price</TableCell>
                          <TableCell>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedOrderForView.items?.map((item, index) => {
                          let productImage = null;
                          if (item.productImage) {
                            productImage = getFullImageUrl(item.productImage);
                          } else if (item.image) {
                            productImage = getFullImageUrl(item.image);
                          } else if (item.imageUrl) {
                            productImage = getFullImageUrl(item.imageUrl);
                          } else if (item.product) {
                            productImage = getProductPrimaryImageUrl(item.product, false, 'small');
                          }
                          
                          return (
                            <TableRow key={index}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  {productImage ? (
                                    <Box
                                      component="img"
                                      src={productImage}
                                      alt={item.productName || item.name}
                                      sx={{
                                        width: 60,
                                        height: 60,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: '1px solid #e0e0e0'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        const fallback = e.target.nextElementSibling;
                                        if (fallback) fallback.style.display = 'flex';
                                      }}
                                    />
                                  ) : null}
                                  <Avatar 
                                    sx={{ 
                                      width: 60, 
                                      height: 60, 
                                      bgcolor: 'grey.300',
                                      display: productImage ? 'none' : 'flex'
                                    }}
                                  >
                                    <Inventory />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      {item.productName || item.name || 'Product'}
                                    </Typography>
                                    {item.category && (
                                      <Chip label={item.category} size="small" sx={{ mt: 0.5 }} />
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Store fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {item.sellerName || item.sellerId || 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">{item.quantity || 0}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1">₹{item.unitPrice?.toLocaleString() || '0'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  ₹{((item.unitPrice || 0) * (item.quantity || 1)).toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Order Summary Section */}
              <Card variant="outlined">
                <CardHeader 
                  title="Order Summary" 
                  avatar={
                    <Avatar sx={{ bgcolor: 'info.main', width: 48, height: 48 }}>
                      <Receipt sx={{ fontSize: 28 }} />
                    </Avatar>
                  }
                  sx={{ bgcolor: 'info.light', '& .MuiCardHeader-title': { color: 'info.dark', fontWeight: 600 } }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                        <Typography variant="body1">₹{selectedOrderForView.subtotal?.toLocaleString() || '0'}</Typography>
                      </Box>
                      {selectedOrderForView.taxAmount && selectedOrderForView.taxAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Tax:</Typography>
                          <Typography variant="body1">₹{selectedOrderForView.taxAmount.toLocaleString()}</Typography>
                        </Box>
                      )}
                      {selectedOrderForView.shippingAmount && selectedOrderForView.shippingAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Shipping:</Typography>
                          <Typography variant="body1">₹{selectedOrderForView.shippingAmount.toLocaleString()}</Typography>
                        </Box>
                      )}
                      {selectedOrderForView.discountAmount && selectedOrderForView.discountAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Discount:</Typography>
                          <Typography variant="body1" color="success.main">
                            -₹{selectedOrderForView.discountAmount.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Total Amount:</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          ₹{selectedOrderForView.totalAmount?.toLocaleString() || '0'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                        <Chip 
                          label={selectedOrderForView.paymentMethod || 'Cash on Delivery'} 
                          size="small"
                          color={selectedOrderForView.paymentMethod === 'COD' ? 'default' : 'primary'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">Payment Status:</Typography>
                        <Chip 
                          label={selectedOrderForView.paymentStatus || 'PENDING'} 
                          size="small"
                          color={
                            selectedOrderForView.paymentStatus === 'PAID' ? 'success' :
                            selectedOrderForView.paymentStatus === 'FAILED' ? 'error' : 'warning'
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Order Rejection Dialog for Pending Approvals */}
      <Dialog open={orderRejectionDialogOpen} onClose={() => setOrderRejectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to reject order {selectedOrderForRejection?.orderNumber}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            value={orderRejectionReason}
            onChange={(e) => setOrderRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejecting this order..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderRejectionDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!orderRejectionReason.trim()) {
                toast.error('Please provide a rejection reason');
                return;
              }
              try {
                await handleRejectOrder(selectedOrderForRejection.id, orderRejectionReason);
                toast.success(`Order ${selectedOrderForRejection.orderNumber} rejected`);
                setOrderRejectionDialogOpen(false);
                setSelectedOrderForRejection(null);
                setOrderRejectionReason('');
                loadPendingOrderApprovals();
              } catch (error) {
                toast.error(error || 'Failed to reject order');
              }
            }}
            disabled={!orderRejectionReason.trim()}
          >
            Reject Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Active Disputes Dialog */}
      <Dialog
        open={activeDisputesDialogOpen}
        onClose={() => setActiveDisputesDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Active Disputes
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Seller</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disputes.map((dispute) => (
                  <TableRow key={dispute.id}>
                    <TableCell>{dispute.orderId}</TableCell>
                    <TableCell>{dispute.customer}</TableCell>
                    <TableCell>{dispute.seller}</TableCell>
                    <TableCell>{dispute.issue}</TableCell>
                    <TableCell>
                      <Chip 
                        label={dispute.priority} 
                        color={dispute.priority === 'High' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={dispute.status} 
                        color={dispute.status === 'Open' ? 'error' : 'info'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{dispute.createdAt}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined">
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActiveDisputesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog
        open={generateReportDialogOpen}
        onClose={() => !reportLoading && setGenerateReportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment color="primary" />
            Generate Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }} disabled={reportLoading}>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={reportType}
                label="Report Type"
                onChange={(e) => setReportType(e.target.value)}
              >
                <MenuItem value="sales">Sales Report</MenuItem>
                <MenuItem value="users">Users Report</MenuItem>
                <MenuItem value="products">Products Report</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth sx={{ mb: 3 }} disabled={reportLoading}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={reportPeriod}
                label="Time Period"
                onChange={(e) => setReportPeriod(e.target.value)}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="quarterly">Quarterly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mb: 2 }}>
              The report will be downloaded as an Excel (.xlsx) file with formatted data and charts.
            </Alert>

            {reportLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Generating report... Please wait.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGenerateReportDialogOpen(false)}
            disabled={reportLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            startIcon={reportLoading ? <CircularProgress size={20} /> : <Download />}
            onClick={handleDownloadReport}
            disabled={reportLoading}
          >
            {reportLoading ? 'Generating...' : 'Download Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Logs Dialog */}
      <Dialog
        open={securityLogsDialogOpen}
        onClose={() => setSecurityLogsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
              <Typography variant="h6">Audit Logs</Typography>
            </Box>
            <IconButton onClick={() => setSecurityLogsDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Filters Section - Collapsible */}
          <Accordion sx={{ mb: 3, boxShadow: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                bgcolor: 'grey.50',
                borderRadius: '4px 4px 0 0',
                '&:hover': {
                  bgcolor: 'grey.100',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <FilterList color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Filters
                </Typography>
                {(logFilterStatus || logFilterTimeRange !== 'recent' || logFilterCustomStartDate || 
                  logFilterCustomEndDate || logFilterUserId || logFilterUserEmail) && (
                  <Chip 
                    label="Active" 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="status-filter-label">Status</InputLabel>
                    <Select
                      value={logFilterStatus}
                      labelId="status-filter-label"
                      label="Status"
                      onChange={(e) => setLogFilterStatus(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            minWidth: 200,
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiSelect-select': {
                          width: '100% !important',
                          minWidth: '200px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          width: '100%',
                        }
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="SUCCESS">Success</MenuItem>
                      <MenuItem value="FAILED">Failed</MenuItem>
                      <MenuItem value="WARNING">Warning</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Time Range Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                      value={logFilterTimeRange}
                      label="Time Range"
                      onChange={(e) => setLogFilterTimeRange(e.target.value)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                            minWidth: 200,
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiSelect-select': {
                          width: '100% !important',
                          minWidth: '200px',
                        }
                      }}
                    >
                      <MenuItem value="recent">Recent (Last 24 hours)</MenuItem>
                      <MenuItem value="last7days">Last 7 Days</MenuItem>
                      <MenuItem value="last15days">Last 15 Days</MenuItem>
                      <MenuItem value="last30days">Last 30 Days</MenuItem>
                      <MenuItem value="last90days">Last 90 Days</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Custom Date Range - Start Date */}
                {logFilterTimeRange === 'custom' && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Start Date"
                      value={logFilterCustomStartDate}
                      onChange={(e) => setLogFilterCustomStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}
                
                {/* Custom Date Range - End Date */}
                {logFilterTimeRange === 'custom' && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="End Date"
                      value={logFilterCustomEndDate}
                      onChange={(e) => setLogFilterCustomEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                )}
                
                {/* User Filter - Email */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="User Email"
                    value={logFilterUserEmail}
                    onChange={(e) => setLogFilterUserEmail(e.target.value)}
                    placeholder="Filter by user email"
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
                
                {/* User Filter - User ID */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="User ID"
                    value={logFilterUserId}
                    onChange={(e) => setLogFilterUserId(e.target.value)}
                    placeholder="Filter by user ID"
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
                
                {/* Reset Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleResetFilters}
                      size="small"
                      startIcon={<Refresh />}
                    >
                      Reset Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          
          {/* Logs Table */}
          {securityLogsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : securityLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No security logs found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {securityLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.timestamp}</TableCell>
                      <TableCell>{log.event}</TableCell>
                      <TableCell>{log.user}</TableCell>
                      <TableCell>
                        <Chip 
                          label={log.userRole || 'UNKNOWN'} 
                          size="small"
                          variant="outlined"
                          color={log.userRole === 'ADMIN' ? 'error' : 
                                 log.userRole === 'SELLER' ? 'warning' : 
                                 log.userRole === 'CUSTOMER' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{log.ipAddress}</TableCell>
                      <TableCell>
                        <Chip 
                          label={log.status} 
                          color={log.status === 'Success' || log.status === 'SUCCESS' ? 'success' : 
                                 log.status === 'Failed' || log.status === 'FAILED' ? 'error' : 
                                 log.status === 'Warning' || log.status === 'WARNING' ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.details}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSecurityLogsDialogOpen(false)}>Close</Button>
          <Button 
            variant="outlined" 
            startIcon={<Download />}
            onClick={handleExportLogs}
            disabled={securityLogs.length === 0}
          >
            Export Logs ({securityLogs.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imagePreview.open}
        onClose={closeImagePreview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
          }}
        >
          <Typography variant="subtitle1">
            {imagePreview.alt || 'Image Preview'}
          </Typography>
          <IconButton onClick={closeImagePreview} aria-label="Close image preview">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
          {imagePreview.src && (
            <Box
              component="img"
              src={imagePreview.src}
              alt={imagePreview.alt || 'Product image'}
              sx={{
                width: '100%',
                height: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminDashboard;